// Le texte générique de la chrome (bouton, titres d'écran, aide), qui ne
// dépend PAS de la formule contrairement à `vocabulaire.ts` — c'est toute la
// différence entre les deux fichiers. « Configuration » ou le sélecteur de
// langue se disent pareil en console et en Radar ; seule la LANGUE varie ici.
//
// Volontairement minuscule pour l'instant (chantier i18n, Phase 0) : seules
// les clés déjà utilisées par l'infrastructure elle-même y sont. Le balayage
// des ~300 chaînes françaises encore en dur dans `pages/*.tsx` et
// `components/*.tsx` (Phase 1, cf. docs/chantier-radar-console.md) les
// ajoutera au fil de l'eau, fichier par fichier — pas d'un coup.
//
// Même patron que `apps/extension/src/lib/i18n.ts` : dictionnaires plats par
// langue, clés `<scope>.<cle>`, repli sur la clé elle-même si absente (« un
// libellé moche vaut mieux qu'un composant qui plante »).

import { useLang, type Lang } from './lang'
import { useFormule } from './formule'
import { DICT_NL } from './i18n.nl'
import { DICT_IT } from './i18n.it'
import { DICT_ES } from './i18n.es'

export const DICT = {
  fr: {
    'acces.fin.titre': 'Votre accès à Radar a pris fin.',
    'acces.essai.fin': 'Votre essai gratuit finit le {date}.',
    'acces.essai.choisir': 'Choisir une formule',
    'acces.fin.texte': 'Vos offres, vos candidatures et votre CV restent consultables. La veille de nuit et les nouveaux dossiers reprendront avec un abonnement.',
    'autreCompte.texte': "Ce lien vient du mail d'un autre compte.",
    'autreCompte.changer': 'Changer de compte',
    'autreCompte.rester': 'Rester ici',
    'abonnement.titre': 'Abonnement',
    'abonnement.sAbonner': "S'abonner",
    'abonnement.redirection': 'Redirection vers le paiement…',
    'abonnement.parMois': 'par mois',
    'abonnement.parAn': 'par an',
    'abonnement.ttc': 'TVA comprise',
    'abonnement.offert': 'Radar vous est offert.',
    'compteurs.titre': 'Votre utilisation',
    'compteurs.cv': 'CV adaptés cette semaine',
    'compteurs.employeurs': 'Employeurs suivis',
    'compteurs.recherches': 'Salaire et entretien ce mois-ci',
    'compteurs.sur': 'sur {max}',
    'compteurs.sansLimite': 'Sans limite',
    'compteurs.repart': 'Repart le {date}',
    'compteurs.atteint': 'Atteint, repart le {date}',
    'compteurs.plein': 'Plafond atteint',
    'compteurs.avecComplet': 'Avec Complet',
    'abonnement.essaiJusqua': 'Essai gratuit jusqu’au {date}.',
    'abonnement.essaiFini': 'Votre essai gratuit a pris fin le {date}.',
    'abonnement.duree': 'Durée',
    'abonnement.auMois': 'Au mois',
    'abonnement.alAnnee': 'À l’année',
    'abonnement.radar': 'Radar',
    'abonnement.complet': 'Complet',
    'abonnement.radar.l1': '20 employeurs suivis',
    'abonnement.radar.l2': '15 CV adaptés par semaine',
    'abonnement.complet.l1': 'Tous les employeurs',
    'abonnement.complet.l2': 'CV adaptés sans limite',
    'abonnement.complet.l3': 'Salaire et entretien à volonté',
    'abonnement.radar.l3': 'Salaire et entretien : 2 par mois',
    'abonnement.radar.l4': '4 modèles de CV',
    'abonnement.complet.l4': 'Ma mise en page, tirée de votre CV',
    'abonnement.choisir': 'Choisir {plan}',
    'abonnement.abonne': 'Vous êtes abonné.',
    'abonnement.abonneJusqua': "Vous êtes abonné. Prochaine échéance : {date}.",
    'abonnement.finLe': 'Votre abonnement prend fin le {date}.',
    'abonnement.gerer': "Carte, factures, résiliation",
    'abonnement.reprendre': "Reprendre l'abonnement",
    'abonnement.enRetard': 'Le dernier paiement a échoué. Vérifiez votre carte.',
    'abonnement.mettreAJour': 'Mettre ma carte à jour',
    'abonnement.confirmation': 'Paiement reçu, confirmation en cours…',
    'abonnement.confirmationLongue': "La confirmation tarde. Stripe vous l'enverra par mail ; rechargez cette page dans quelques minutes.",
    'abonnement.annule': "Paiement annulé. Rien n'a changé.",
    'abonnement.pasOuvert': "Le paiement n'est pas encore ouvert.",
    'nav.config': 'Configuration',
    'lang.toggle.label': 'Langue de l’interface',
    'nav.mainAria': 'Navigation principale',
    'nav.logout': 'Se déconnecter',
    'nav.accountFallbackRadar': 'Mon compte',
    'nav.accountFallbackConsole': 'Business Manager',
    'nav.consultantsCount': '{n} consultants',

    'login.radar.accroche': 'Les offres faites pour vous, chaque matin.',
    'login.modes': 'Se connecter ou créer un compte',
    'login.ou': 'ou',
    'login.mdp.voir': 'Afficher le mot de passe',
    'login.mdp.masquer': 'Masquer le mot de passe',
    'inscription.titre': 'Créer un compte',
    'inscription.mdp': 'Choisissez un mot de passe',
    'inscription.mdpAide': '{n} caractères au moins.',
    'inscription.envoyer': 'Créer mon compte',
    'inscription.envoi': 'Envoi…',
    'inscription.essai': 'Un mois gratuit, sans carte.',
    'inscription.robot': 'Cochez la vérification anti-robots.',
    'inscription.parti.texte': 'Un lien vient de partir à {email}. Ouvrez-le pour activer votre compte.',
    'inscription.autreAdresse': 'Utiliser une autre adresse',
    'inscription.activation': 'Activation de votre compte…',
    'inscription.lienInvalide': 'Ce lien n’est plus valable. Recommencez l’inscription.',
    'login.subtitle.console': 'Console consultance',
    'login.subtitle.admin': 'Administration',
    'login.subtitle.radar': 'Radar',
    'login.email': 'Adresse e-mail',
    'login.password': 'Mot de passe',
    'login.submitting': 'Connexion…',
    'login.submit': 'Se connecter',
    'login.error.generic': 'Connexion impossible.',
    'login.oubli.lien': 'Mot de passe oublié ?',
    'login.google.bouton': 'Continuer avec Google',
    'login.google.sans_compte': "Aucun compte pour cette adresse Google. L'accès se fait sur invitation.",
    'login.google.expire': 'La connexion avec Google a expiré. Réessayez.',
    'login.google.annule': 'Connexion avec Google annulée.',
    'login.google.erreur': 'Google n’a pas pu confirmer votre identité. Réessayez.',
    'login.google.ferme': 'Les inscriptions sont fermées pour le moment.',
    'login.google.inscription': 'Pas encore de compte ? Google le crée. Un mois gratuit, sans carte.',
    'login.google.refuse': 'Impossible de créer un compte avec cette adresse Google.',
    'login.oubli.titre': 'Votre adresse',
    'login.oubli.envoyer': 'Recevoir un lien de connexion',
    'login.oubli.parti': "Si un compte existe à cette adresse, un lien vient de partir. Il vaut 30 minutes.",
    'accueil.step': 'Étape {n} sur {total}',
    'accueil.next': 'Suivant',
    'accueil.back': 'Précédent',
    'accueil.finish': 'Terminer',
    'accueil.done': 'Bienvenue. Tout est dans « Prise en main » si vous voulez relire.',
    'accueil.radar.title': 'Radar cherche pendant que vous faites autre chose',
    'accueil.radar.lead': 'Des sites relus chaque nuit, à votre place.',
    'accueil.radar.body': "Vous choisissez des sites d'offres. Chaque nuit, Radar les relit, garde ce qui est nouveau, compare chaque annonce à votre CV et lui donne un score. Le matin, vous ne cherchez pas : vous triez ce qui est remonté.",
    'accueil.radar.body2': "Une offre qui vous intéresse s'ouvre en fiche : le score détaillé, ce qui correspond, ce qui manque, et de quoi générer un CV adapté et une lettre.",
    'accueil.radar.shot1Alt': "Écran « Mes opportunités » : une liste d'offres avec leur score",
    'accueil.radar.shot2Alt': "Fiche d'une offre : score détaillé, points forts et manques",
    'accueil.extension.title': "L'extension Chrome travaille là où vous êtes",
    'accueil.extension.lead': "Sur LinkedIn, sur Indeed, sur le site d'un employeur : partout.",
    'accueil.extension.body': "Elle est facultative, mais c'est elle qui vous accompagne sur les annonces : quand vous en regardez une, elle affiche votre score dessus, adapte votre CV à cette offre et rédige une lettre. Un clic l'envoie aussi dans Radar, pour y revenir plus tard.",
    'accueil.extension.videoNote': 'Deux minutes, et vous verrez exactement à quoi ça ressemble.',
    'accueil.ensemble.title': 'Deux outils indépendants, meilleurs ensemble',
    'accueil.ensemble.lead': 'Un qui cherche pour vous, un qui vous suit quand vous cherchez.',
    'accueil.ensemble.passifTitle': 'Radar, le mode qui tourne tout seul',
    'accueil.ensemble.passifBody': "Il relit vos sites toutes les nuits et vous prépare une pile. Vous n'avez rien à faire pour qu'il travaille.",
    'accueil.ensemble.actifTitle': "L'extension, le mode du moment",
    'accueil.ensemble.actifBody': "Elle agit sur l'annonce que vous avez sous les yeux, y compris sur un site que Radar ne surveille pas.",
    'accueil.ensemble.body': "Branchés l'un à l'autre, un bouton apparaît sur chaque annonce : un clic, et elle entre dans Radar avec son score. L'offre vue sur LinkedIn à midi est dans votre historique le soir, et vous déciderez d'y répondre quand vous aurez le temps.",
    'accueil.ensemble.independants': "Vous pouvez n'utiliser que l'un des deux. Rien ne dépend de l'autre.",
    'accueil.cv.title': 'Votre CV, une fois',
    'accueil.cv.lead': "C'est la référence à laquelle tout est comparé.",
    'accueil.cv.body': "Déposez-le en PDF. Il est lu et structuré une seule fois, puis sert à scorer chaque offre et à produire les CV adaptés. Sans lui, rien ne peut être comparé et vos écrans resteront vides.",
    'accueil.cv.cta': 'Déposer mon CV',
    'accueil.cv.done': 'Votre CV est en place.',
    'accueil.metiers.title': 'Vos métiers',
    'accueil.metiers.lead': 'Voici les métiers que nous avons lus dans votre CV.',
    'accueil.metiers.body': "Radar ne vous évalue que les offres de ces métiers. Retirez ce qui ne vous ressemble pas, ajoutez ce qui manque : tapez un métier et choisissez dans la liste.",
    'accueil.metiers.enCours': 'Lecture de votre CV en cours, quelques secondes…',
    'accueil.metiers.needCv': "Déposez d'abord votre CV à l'étape précédente.",
    'accueil.metiers.later': 'Vous pourrez les changer à tout moment sur votre fiche profil.',
    'accueil.sites.title': 'Choisissez vos sites',
    'accueil.sites.lead': "Rien n'est coché : c'est à vous de dire ce que vous voulez suivre.",
    'accueil.sites.body': "Ces sites sont ceux que ce Radar sait déjà lire. Cochez ceux qui vous concernent : vous ne verrez que leurs offres, et vous ne gênerez personne en changeant d'avis.",
    'accueil.sites.needCv': "Déposez votre CV à l'étape précédente pour pouvoir choisir : les sites se rattachent à votre profil.",
    'accueil.sites.offers': '{n} offres',
    'accueil.sites.all': 'Tout cocher',
    'accueil.sites.none': 'Tout décocher',
    'accueil.sites.count': '{n} sites choisis',
    'accueil.sites.countMax': '{n} / {max} sites choisis',
    'accueil.sites.plafond': '{max} sites au plus : décochez-en un pour en choisir un autre.',
    'accueil.sites.later': "Vous pourrez en ajouter, en retirer, ou en demander de nouveaux à tout moment dans « Sites suivis ».",
    'accueil.revoir': 'Revoir la présentation',
    'aide.intro': "Ce qu'est Radar, comment brancher l'extension, et ce qui ne marchera pas du premier coup. Cette page reste là : revenez-y quand vous voulez.",
    'aide.what.title': 'Ce que Radar fait pour vous',
    'aide.what.body': "Radar surveille des pages d'offres d'emploi, chaque nuit, à votre place. Chaque offre trouvée est comparée à votre CV et reçoit un score. Vous ne cherchez plus : vous regardez ce qui est remonté, et vous décidez.",
    'aide.what.extension': "L'extension Chrome est l'autre moitié : elle fonctionne sur n'importe quel site d'offres, affiche votre score directement sur l'annonce, adapte votre CV, et envoie l'offre dans Radar en un clic. Une annonce vue sur LinkedIn le midi se retrouve dans Radar le soir, avec son score, sans que vous ayez rien noté.",
    'aide.step.cv.title': '1. Votre CV',
    'aide.step.cv.body': "Tout part de là : sans CV, rien ne peut être comparé. Déposez-le en PDF, il est lu et structuré une fois. Vous pourrez le remplacer à tout moment.",
    'aide.step.cv.cta': 'Déposer ou mettre à jour mon CV',
    'aide.step.install.title': "2. Installer l'extension",
    'aide.step.install.body': "Gratuite, sur le Chrome Web Store. Un clic, rien à régler.",
    'aide.step.install.store': "Ajouter à Chrome",
    'aide.step.install.storeS1': "Sur la fiche, cliquez « Ajouter à Chrome », puis « Ajouter l'extension ».",
    'aide.step.install.zipTitre': "Installer sans le Chrome Web Store",
    'aide.step.install.zipQuand': "Seulement si le store vous est bloqué, sur un poste d'entreprise par exemple.",
    'aide.step.link.autrement': "Pas de bouton « Connecter avec Radar » ? Connectez-vous avec votre adresse et votre mot de passe Radar, ou avec le code ci-dessous.",
    'accueil2.extension.texte': "Votre score sur chaque offre, LinkedIn compris : l'extension est sur le Chrome Web Store.",
    'accueil2.extension.plusTard': "Plus tard",
    'aide.step.install.download': "Télécharger l'extension (version {version})",
    'aide.step.install.unavailable': "Le paquet de la version {version} n'est pas encore prêt. Signalez-le en bas de cette page, vous recevrez un message dès qu'il est disponible.",
    'aide.step.install.s1': "Décompressez le fichier téléchargé dans un dossier que vous garderez : Chrome lira l'extension depuis là, la corbeille la désinstallerait.",
    'aide.step.install.s2': "Ouvrez chrome://extensions dans un nouvel onglet, puis activez « Mode développeur » en haut à droite.",
    'aide.step.install.s3': "Cliquez « Charger l'extension non empaquetée » et désignez le dossier « ai-job-fit-{version} » qui se trouve DANS le dossier décompressé, pas le dossier décompressé lui-même. Si Chrome répond « Manifest introuvable », c'est qu'il manque ce niveau.",
    'aide.step.install.s4': "Épinglez l'icône à la barre d'outils : c'est par elle que vous activerez l'extension sur les sites qu'elle ne connaît pas encore.",
    'aide.step.install.sites': "Elle s'active toute seule sur LinkedIn, Indeed et les grands outils de recrutement. Ailleurs, Chrome exige votre accord site par site : ouvrez l'annonce, cliquez l'icône de l'extension, puis « Activer sur ce site ». C'est une règle de Chrome, pas une limite de l'outil.",
    'aide.step.link.title': "3. Brancher l'extension sur Radar",
    'aide.step.link.body': "Votre CV vient alors de Radar : rien à importer dans l'extension, et c'est ici que vous le modifiez.",
    'aide.step.link.lastUsed': 'Extension connectée. Dernier envoi : {date}.',
    'aide.step.link.neverUsed': "Extension connectée, mais aucune offre n'est encore arrivée par elle.",
    'aide.step.link.s1': "Ouvrez l'extension : au premier lancement, elle s'ouvre seule. Sinon, clic droit sur son icône, puis « Options ».",
    'aide.step.link.s2': "Sous l'import du CV, cliquez « Vous avez un compte Radar ? Connectez-vous », puis « Connecter avec Radar ». Chrome demande l'accès à Radar : acceptez.",
    'aide.step.link.s3': "Votre CV apparaît dans l'extension, et un bouton « Enregistrer dans Radar » s'affiche sur chaque offre.",
    'aide.step.sites.title': '4. Ajouter un site à surveiller',
    'aide.step.sites.body': "Collez l'adresse d'une page qui LISTE des offres, pas d'une annonce seule. Radar teste l'extraction devant vous et montre les intitulés trouvés avant d'enregistrer quoi que ce soit.",
    'aide.step.sites.warning': "À savoir d'avance : ça ne marchera pas du premier coup sur tous les sites. Radar reconnaît seul les grandes plateformes ; tout autre site demande une ligne de configuration écrite à la main. Dans ce cas, un bouton « demander l'activation » apparaît : la demande est enregistrée, vous recevez un e-mail de confirmation, puis un second quand le site est en ligne, en général sous une semaine. Ce n'est pas une panne, c'est le fonctionnement normal.",
    'aide.step.sites.cta': 'Aller à mes sites surveillés',
    'aide.shared.title': "Ce qui est partagé, ce qui ne l'est pas",
    'aide.shared.body': "Les sites surveillés et les offres collectées sont COMMUNS aux comptes de ce Radar : vous voyez les sites ajoutés par les autres, et ils voient les vôtres. Votre CV, vos scores, vos candidatures et vos documents, eux, ne sont visibles que de vous.",
    'aide.feedback.title': 'Un mot à qui développe Radar',
    'aide.feedback.body': "Quelque chose ne marche pas, un écran est confus, il manque une fonction, un site n'est pas pris en charge : écrivez-le ici. Le message arrive directement par e-mail, avec votre adresse pour que la réponse vous revienne.",
    'aide.feedback.typeLabel': 'De quoi il s\'agit',
    'aide.feedback.type.bug': 'Quelque chose ne marche pas',
    'aide.feedback.type.idea': 'Une idée, une amélioration',
    'aide.feedback.type.site': 'Un site à prendre en charge',
    'aide.feedback.type.other': 'Autre chose',
    'aide.feedback.messageLabel': 'Votre message',
    'aide.feedback.placeholder': 'Ce que vous faisiez, ce que vous attendiez, ce qui est arrivé à la place.',
    'aide.feedback.send': 'Envoyer',
    'aide.feedback.sending': 'Envoi…',
    'aide.feedback.sent': 'Message envoyé. Merci, vous aurez une réponse à votre adresse.',
    'aide.feedback.stored': "Message enregistré, mais l'e-mail n'est pas parti. Il sera lu quand même.",

    'password.title': 'Choisissez votre mot de passe',
    'password.intro': 'Ce compte a été ouvert avec un mot de passe provisoire. Il faut le remplacer avant d’aller plus loin.',
    'password.current': 'Mot de passe provisoire',
    'password.next': 'Nouveau mot de passe',
    'password.confirm': 'Confirmation',
    'password.rule': 'Au moins {n} caractères. Aucune autre règle : une phrase que vous retenez vaut mieux qu’un assemblage de symboles.',
    'password.submit': 'Enregistrer et continuer',
    'password.submitting': 'Enregistrement…',
    'password.tooShort': 'Le nouveau mot de passe doit faire au moins {n} caractères.',
    'password.mismatch': 'Les deux saisies ne correspondent pas.',
    'password.error.generic': 'Changement impossible.',
    'password.error.same': "Le nouveau mot de passe doit être différent de l'actuel.",
    'password.logout': 'Me déconnecter',

    'invite.title': 'Créez votre compte',
    'invite.intro': 'Ce lien ouvre un compte {produit} à l’adresse ci-dessous. Choisissez votre mot de passe pour commencer.',
    'invite.email': 'Adresse du compte',
    'invite.submit': 'Créer mon compte',
    'invite.submitting': 'Création…',
    'invite.loading': 'Vérification du lien…',
    'invite.gone.title': 'Ce lien n’est plus valable',
    'invite.gone.expiree': 'Il a expiré. Demandez un nouveau lien à la personne qui vous l’a envoyé.',
    'invite.gone.utilisee': 'Il a déjà servi à créer un compte. Connectez-vous avec l’adresse et le mot de passe choisis à ce moment-là.',
    'invite.gone.revoquee': 'Il a été remplacé ou annulé. Demandez un nouveau lien à la personne qui vous l’a envoyé.',
    'invite.gone.inconnue': 'Ce lien est incomplet ou erroné. Vérifiez qu’il a été copié en entier.',
    'invite.gone.compte_existant': 'Un compte existe déjà à cette adresse. Connectez-vous avec son mot de passe.',
    'invite.otherProduct': 'Ce lien ouvre un compte sur un autre site.',
    'invite.otherProductOpen': 'Ouvrir le bon site',
    'invite.toLogin': 'Aller à la connexion',
    'invite.error.generic': 'Création du compte impossible.',

    'candidature.stepToast': 'Étape : {label}',
    'candidature.propose': 'Proposer',
    'candidature.proposeName': 'Proposer {name}',
    'candidature.proposedToast': '{name} proposé, la candidature est dans le suivi',

    'common.loading': 'Chargement…',
    'common.close': 'Fermer',
    'common.cancel': 'Annuler',
    'common.update': 'Mettre à jour',
    'common.import': 'Importer',

    'importCv.updateTitle': 'Mettre à jour {name}',
    'importCv.importing': 'Import…',
    'importCv.needFile': 'Choisis un fichier PDF',
    'importCv.updatedToast': 'Profil de {name} mis à jour',
    'importCv.fileLabel': 'CV (PDF)',
    'importCv.depot': 'Glissez le PDF ici, ou cliquez pour le choisir',
    'importCv.fileHelp': "Extraction du texte, structuration du profil par l'IA, puis vectorisation pour le matching (~10 s). L'intitulé du poste est lu dans le CV.",
    'importCv.nameLabel': 'Nom (optionnel)',
    'importCv.namePlaceholder': 'Sinon : lu dans le CV',

    'gapQuestions.title': 'Avant de générer, {n} précisions',
    'gapQuestions.titleOne': 'Avant de générer, une précision',
    'gapQuestions.skip': 'Passer',
    'gapQuestions.generating': 'Génération…',
    'gapQuestions.introPlural': "L'offre demande des choses que le CV ne dit pas explicitement. Répondre ou non ne change pas si le CV est adapté, seulement ce qu'il pourra valoriser.",
    'gapQuestions.introSingular': "L'offre demande une chose que le CV ne dit pas explicitement. Répondre ou non ne change pas si le CV est adapté, seulement ce qu'il pourra valoriser.",
    'gapQuestions.placeholder': 'Une expérience à mentionner, même ponctuelle ? Sinon, laissez vide.',
    'gapQuestions.answeredNone': "Rien de rempli pour l'instant, l'adaptation partira sans enrichissement.",
    'gapQuestions.answeredPlural': '{n} réponses seront prises en compte.',
    'gapQuestions.answeredSingular': '1 réponse sera prise en compte.',
    'gapQuestions.noneNeeded': 'Pas de question : le CV couvre déjà l’offre.',

    'vivier.loading': 'Chargement du vivier…',
    'vivier.wentOnMission': '{name} passé en mission',
    'vivier.backOnBench': '{name} revenu sur le banc',
    'vivier.archivedToast': '{name} archivé, historique conservé',
    'vivier.reactivatedToast': '{name} réactivé',
    'vivier.summary': '{available} disponibles · {onMission} en mission · {archived} archivé(s)',
    'vivier.showActive': 'Voir le vivier actif',
    'vivier.showArchived': 'Archivés ({n})',
    'vivier.importCv': 'Importer un CV',
    'vivier.avgScore': 'Score moy.',
    'vivier.evaluated': 'Évalué',
    'vivier.positioned': 'Positionné',
    'vivier.updated': 'Maj',
    'vivier.reactivate': 'Réactiver',
    'vivier.onMission': 'En mission',
    'vivier.onBench': 'Sur le banc',
    'vivier.archive': 'Archiver',
    'vivier.dropzoneTitle': 'Importer un CV PDF',
    'vivier.dropzoneHint': 'Extraction → profil structuré → vectorisation. Évalué sur les offres à venir.',
    'vivier.noArchived': 'Aucun consultant archivé.',

    'dossier.revert': '↩ rétablir',
    'dossier.defautEnregistre': 'Réglages enregistrés pour vos prochains CV.',
    'modeles.sobre': 'Sobre',
    'modeles.dates': 'Dates à gauche',
    'modeles.colonnes': 'Deux colonnes',
    'modeles.colonnes.note': 'Deux colonnes : certains logiciels de recrutement la lisent mal.',
    'modeles.compact': 'Compact',
    'modeles.perso': 'Ma mise en page',
    'modeles.perso.complet': 'Avec Complet',
    'modeles.perso.note': 'Tirée de votre CV. Le Word reste en mise en page standard.',
    'modeles.perso.enCours': 'En préparation',
    'modeles.perso.aPreparer': 'À préparer dans Mon CV',
    'modeles.parDefaut': 'Ce modèle servira pour vos prochains CV.',
    'config.modeleLabel': 'Mise en page',
    'config.modele.sobreEffet': 'Une colonne, que tous les logiciels de recrutement lisent.',
    'config.modele.datesEffet': 'Les dates dans une colonne, à gauche de chaque poste.',
    'config.modele.compactEffet': 'Plus serré, pour tenir sur moins de pages.',
    'config.modele.persoEffet': 'Tirée de votre CV, avec Radar Complet.',
    'miseEnPage.titre': 'Ma mise en page',
    'miseEnPage.texte': 'Vos CV adaptés peuvent reprendre la mise en page de votre CV.',
    'miseEnPage.prete': 'Prête.',
    'miseEnPage.choix': 'Le modèle de vos prochains CV, le même que dans les Réglages.',
    'miseEnPage.enCours': 'En préparation…',
    'miseEnPage.echec': 'Elle n’a pas pu être tirée de votre CV.',
    'miseEnPage.preparer': 'Préparer ma mise en page',
    'miseEnPage.refaire': 'Refaire ma mise en page',
    'miseEnPage.sansPdf': 'Déposez votre CV en PDF pour en tirer la mise en page.',
    'miseEnPage.complet': 'Avec Radar Complet.',
    'miseEnPage.formules': 'Voir les formules',
    'miseEnPage.essai': 'Votre essai en comprend une.',
    'dossier.revertedToast': 'Phrase rétablie, audit recalculé',
    'dossier.loadingFile': 'Chargement du document…',
    'dossier.notFound': 'Document introuvable',
    'dossier.regeneratingFile': 'Nouvelle version en cours…',
    'dossier.generatedOn': 'Généré {date}',
    'dossier.ouvrirCv': 'Ouvrir le CV',
    'dossier.savedToast': 'CV enregistré, audit recalculé',

    'categories.loadError': 'Chargement impossible',
    'categories.title': 'Catégories',
    'categories.close': 'Fermer',
    'categories.edit': 'Modifier',
    'categories.empty': "Aucune catégorie. Tant qu'il n'y en a pas, cette fiche n'est rapprochée d'aucune offre.",
    'categories.originHuman': 'corrigée',
    'categories.originAi': 'proposée',
    'categories.neighbours': 'Catégories voisines',
    'categories.neighboursHelp': "Les groupes frères de ceux ci-dessus. Ils ne déclenchent pas d'évaluation automatique, mais ils disent qui est juste à côté.",
    'categories.selectedEmpty': "Aucun métier pour l'instant. Tapez-en un ci-dessous.",
    'categories.remove': 'Retirer',
    'categories.searchLabel': 'Ajouter un métier',
    'categories.searchPlaceholder': 'Tapez un métier : développeur, infirmière, comptable…',
    'categories.noResult': 'Aucun métier trouvé. Essayez un autre mot.',
    'categories.saving': 'Enregistrement…',
    'categories.save': 'Enregistrer',
    'categories.savedToast': 'Catégories enregistrées',

    'suivi.loading': 'Chargement du suivi…',
    'suivi.activeCountPlural': '{n} candidatures en cours',
    'suivi.activeCountSingular': '{n} candidature en cours',
    'suivi.setAsideAt': 'remise à plus tard {when}',
    'suivi.putBackTitle': 'Remettre dans « {name} »',
    'suivi.putBackToast': '« {title} » remise dans « {name} »',
    'suivi.closedSummary': '{placed} {placedLabel}, {refused} refusée(s)',
    'suivi.offerGoneTitle': "L'annonce a disparu du site",
    'suivi.offerExpired': 'annonce expirée',
    'suivi.noMovementTitle': 'Aucun mouvement depuis {n} jours',
    'suivi.daysShort': '{n} j',
    'suivi.openFileTitle': 'Ouvrir : {name}',
    'suivi.noFileTitle': "Aucun CV adapté n'a été généré pour cette candidature",
    'suivi.originalPosting': "Annonce d'origine",
    'suivi.nextSend': 'Envoyer la candidature',
    'suivi.preuveTitre': 'Mes candidatures envoyées',
    'suivi.preuveAide': 'Date, employeur et poste, pour le Forem, Actiris ou le VDAB.',
    'suivi.preuveCopier': 'Copier la liste',
    'suivi.preuveCopiee': 'Liste copiée.',
    'suivi.preuveEchec': 'Copie impossible depuis ce navigateur.',
    'suivi.nextFollowUp': 'Relancer le {date}',
    'suivi.nextFollowUpDue': 'Relancer maintenant (prévu le {date})',
    'suivi.nextInterview': "Préparer l'entretien",
    'suivi.expiredTitle': 'Expirées',
    'suivi.expiredHint': "Offres retirées du site avant l'envoi",
    'suivi.abandon': 'Abandonner',
    'suivi.abandonConfirm': 'Abandonner cette candidature ?',
    'suivi.abandonCancel': 'Annuler',
    'suivi.removeAriaTitle': 'Retirer « {title} » du suivi',
    'suivi.removeAriaName': 'Retirer {name} du suivi',
    'suivi.removedToastTitle': '« {title} » retiré du suivi',
    'suivi.removedToastName': '{name} retiré du suivi',

    'veille.periodAll': 'Toutes dates',
    'veille.periodToday': "Aujourd'hui",
    'veille.period7d': '7 derniers jours',
    'veille.period30d': '30 derniers jours',
    'veille.loading': 'Chargement de la veille…',
    'veille.subtitle': 'Tout ce que les sources ont ramené · {n} offres suivies',
    'veille.subtitleSingular': 'Tout ce que les sources ont ramené · {n} offre suivie',
    'veille.searchPlaceholder': 'Rechercher…',
    'veille.searchAria': 'Rechercher',
    'veille.clearSearch': 'Effacer la recherche',
    'veille.manualEntryButton': 'Saisie manuelle',
    'veille.allCompanies': 'Toutes entreprises',
    'veille.allLocations': 'Tous lieux',
    'veille.allConsultants': 'Tous consultants',
    'veille.periodAria': 'Période',
    'veille.statusAria': 'Statut',
    'veille.allStatuses': 'Tous statuts',
    'veille.shownPlural': '{n} offres affichées',
    'veille.shownSingular': '{n} offre affichée',
    'veille.reset': 'Réinitialiser ({n})',
    'veille.colTitle': 'Offre',
    'veille.colCompany': 'Entreprise',
    'veille.colDetected': 'Détectée',
    'veille.colStatus': 'État',
    'veille.unread': 'Non lue',
    'veille.missionBadge': 'Mission',
    'veille.viewOriginal': "Voir l'annonce d'origine",
    'veille.manualPlatform': 'saisie manuelle',
    'veille.noMatch': 'Aucune offre ne correspond à ces filtres.',
    'veille.resetFilters': 'Réinitialiser les filtres',
    'veille.evaluating': 'Évaluation…',
    'veille.addedToast': 'Offre ajoutée, {n} profil(s) évalué(s)',
    'veille.addAndEvaluate': 'Ajouter et évaluer',
    'veille.jobTitleLabel': 'Intitulé du poste',
    'veille.jobTitlePlaceholder': 'Ex. Senior Backend Engineer, Java',
    'veille.companyLabel': 'Client / entreprise',
    'veille.locationLabel': 'Localisation',
    'veille.optional': 'Optionnel',
    'veille.briefLabel': 'Cahier des charges',
    'veille.briefPlaceholder': 'Colle ici la description complète…',
    'veille.briefHelp': "Au moins 100 caractères. L'offre est évaluée immédiatement.",

    'consultant.loading': 'Chargement de la fiche…',
    'consultant.backToBench': 'Retour au vivier',
    'consultant.updatedOn': 'mis à jour le {date}',
    'consultant.printPdf': 'Imprimer / PDF',
    'consultant.noDocument': 'Aucun document.',
    'consultant.originalPdf': "PDF d'origine",
    'consultant.cvEdit': 'Modifier',
    'consultant.cvSave': 'Enregistrer',
    'consultant.cvSaving': 'Enregistrement…',
    'consultant.cvCancel': 'Annuler',
    'consultant.cvSaved': 'CV enregistré',
    'consultant.cvDiscard': 'Abandonner les modifications non enregistrées ?',
    'consultant.keywordsTitle': 'Ce qui revient dans vos offres',
    'consultant.keywordsIntroPlural': 'Sur les {n} offres déjà évaluées pour vous.',
    'consultant.keywordsIntroSingular': 'Sur la {n} offre déjà évaluée pour vous.',
    'consultant.keywordsCovered': 'Ce que votre CV couvre déjà',
    'consultant.keywordsMissing': 'Ce qui manquait le plus souvent',
    'consultant.filesTotal': '{n} au total',
    'consultant.offerArchived': 'offre archivée',
    'consultant.fidelityTitle': 'Fidélité au CV maître : {n} %',
    'consultant.noEvent': "Aucun événement pour l'instant.",
    'consultant.eventFidelity': ', fidélité {n} %',
    'consultant.eventClient': ', {client}',

    'sources.addedToast': '{name} ajouté, {n} offres insérées',
    'sources.alreadyKnownToast': 'Radar surveillait déjà ce site ({name}) : vous le suivez maintenant',
    'sources.noneFollowed': "Vous ne suivez encore aucun site. Collez l'adresse d'une page carrière ci-dessus, ou parcourez les sites que Radar surveille déjà.",
    'sources.browseTitle': 'Parcourir les {n} sites déjà surveillés par Radar',
    'sources.browseHelp': "Ils sont surveillés pour d'autres comptes. Suivez ceux qui vous intéressent : leurs offres seront comparées à votre CV.",
    'sources.browseSearch': 'Chercher un site ou une entreprise',
    'sources.browseNone': 'Aucun site ne correspond. Ajoutez-le par son adresse, ci-dessus.',
    'sources.loading': 'Chargement des sources…',
    'sources.title': 'Sources',
    'sources.activeSites': '{n} sites actifs',
    'sources.followedSites': '{n} sites suivis',
    'sources.followedSitesMax': '{n} / {max} sites suivis',
    'sources.errorCount': '{n} en erreur',
    'sources.watchedNightly': 'veille chaque nuit',
    'sources.addTitle': "Ajouter un site, colle l'URL de sa page carrière",
    'sources.urlPlaceholder': 'https://jobs.exemple.be',
    'sources.urlAria': 'URL de la page carrière',
    'sources.detect': 'Détecter',
    'sources.detecting': "Reconnaissance de la plateforme et test d'extraction…",
    'sources.unreachablePrefix': 'Page',
    'sources.unreachable': 'injoignable',
    'sources.unreachableSuffix': ", vérifie l'URL.",
    'sources.unsupportedPrefix': 'Plateforme',
    'sources.unsupported': 'pas encore prise en charge',
    'sources.unsupportedSuffix': ". Couvertes aujourd'hui :",
    'sources.requestExplain': "Ce n'est pas la fin : demandez l'activation ci-dessous. Un site inconnu demande une ligne de configuration écrite à la main, c'est la seule chose qui ne se devine pas. Vous recevrez un e-mail quand ce sera en ligne.",
    'sources.follow': 'Suivre',
    'sources.logoLabel': 'Logo',
    'sources.logoUrlPlaceholder': "Adresse d'une image (https://…)",
    'sources.logoSet': 'Utiliser',
    'sources.logoFile': 'Envoyer un fichier',
    'sources.logoAuto': 'Reprendre le favicon du site',
    'sources.logoHelp': "Un logo posé à la main n'est plus jamais remplacé par la recherche automatique. PNG, SVG, ICO, JPEG ou WebP, 512 Ko au plus.",
    'sources.logoUpdated': 'Logo mis à jour.',
    'sources.unfollow': 'Ne plus suivre',
    'sources.followed': '{name} : vous suivez ce site. Radar lit et évalue ses offres, la progression s\'affiche en haut de l\'écran.',
    'sources.followedStale': '{name} : vous suivez ce site. Personne ne le suivait, ses offres datent de sa dernière lecture ({date}) : Radar le relit maintenant, puis évalue ses offres.',
    'sources.notReadSince': 'Lu le {date}, relu dès que vous le suivez',
    'sources.neverRead': 'Lu dès que vous le suivez',
    'sources.staleState': 'Offres du {date}, relecture en attente',
    'accueil.sites.staleHelp': 'Les sites marqués « pas relu » le seront dès que vous aurez terminé, avant d\'évaluer leurs offres.',
    'offre.staleSource': 'Lue le {date}, pas relue depuis : elle est peut-être déjà fermée',
    'offre.staleShort': 'pas relue depuis le {date}',
    'offre.republished': 'republiée (retirée le {date})',
    'offre.republishedMany': 'publiée {n} fois en 90 jours',
    'offre.oldPosting': 'en ligne depuis {n} jours',
    'taches.cv': 'Votre CV',
    'taches.phase.attente': 'en attente',
    'taches.phase.lecture': 'lecture des offres',
    'taches.phase.classement': 'classement par métier',
    'taches.phase.evaluation': 'évaluation des offres',
    'taches.phase.relecture': 'relecture des sites',
    'taches.unite.relecture': 'sites relus : {fait} sur {total}',
    'taches.unite.lecture': 'pages lues : {fait} sur {total}',
    'taches.unite.classement': 'offres classées : {fait} sur {total}',
    'taches.unite.evaluation': 'offres évaluées : {fait} sur {total}',
    'taches.fini': 'terminé, {n} offre(s) évaluée(s)',
    'taches.finiEnAttente': 'terminé, {n} offre(s) évaluée(s) ; {m} pas encore lisible(s), évaluée(s) cette nuit',
    'taches.erreur': 'interrompu ({message}), la nuit reprendra',

    'sources.unfollowed': '{name} : vous ne suivez plus ce site. Ses offres disparaissent de vos écrans, elles restent pour les autres.',
    'sources.followedLabel': 'Suivi',
    'sources.notFollowedLabel': 'Pas suivi',
    'sources.request': "Demander l'activation",
    'sources.requesting': 'Envoi…',
    'sources.requestNotePlaceholder': 'Optionnel : une précision, par exemple la page qui liste les offres',
    'sources.requestNoteAria': 'Précision pour la demande',
    'sources.requestSent': 'Demande enregistrée. Vous recevez un e-mail de confirmation, et un second quand le site est en ligne, en général sous {n} jours.',
    'sources.requestSentNoMail': "Demande enregistrée, mais l'e-mail de confirmation n'est pas parti. Elle est bien dans la liste ci-dessous.",
    'sources.requestFailed': 'Demande impossible.',
    'sources.pendingTitle': "Sites en attente d'activation",
    'sources.pendingHelp': "Demandés, pas encore surveillés. Vous recevrez un e-mail dès que c'est en ligne.",
    'sources.pendingNoMail': 'aucun e-mail envoyé pour cette demande',
    'sources.recognizedPrefix': 'Plateforme reconnue :',
    'sources.viaHtml': '(en lisant la page)',
    'sources.offersFound': '{n} offres trouvées',
    'sources.preview': 'aperçu',
    'sources.actuallyWatched': 'Portail réellement surveillé :',
    'sources.adding': 'Ajout…',
    'sources.addToWatch': 'Ajouter à la veille',
    'sources.addAnyway': 'Ajouter quand même',
    'sources.colCompany': 'Entreprise',
    'sources.colPlatform': 'Plateforme',
    'sources.colHealth': 'Santé',
    'sources.colLastCheck': 'Dernière vérif',
    'sources.colOffers': 'Offres',
    'sources.errorLabel': 'Erreur, {detail}',
    'sources.paused': 'En pause',
    'sources.active': 'Actif',
    'sources.reactivatedToast': 'Source réactivée',
    'sources.pausedToast': 'Source mise en pause',
    'sources.resume': 'Reprendre',
    'sources.pause': 'Pause',
    'sources.removeTitle': 'Retirer de la veille',
    'sources.removedTitle': 'Retirées de la veille',
    'sources.removedAt': 'retirée {when}',
    'sources.wouldComeBack': '{n} offres reviendraient',
    'sources.restoredToast': '{name} remise en veille, {n} offre(s) rendues',
    'sources.restoreToWatch': 'Remettre en veille',
    'sources.removedToast': '{name} retirée, {n} offre(s) archivées',
    'sources.editTitle': 'Modifier {name}',
    'sources.detecting2': 'Détection…',
    'sources.saving': 'Enregistrement…',
    'sources.updatedToast': 'Source mise à jour',
    'sources.save': 'Enregistrer',
    'sources.companyLabel': 'Entreprise',
    'sources.companyHelp': 'Le nom affiché partout ailleurs : sur les offres, dans le suivi, et dans le nom des CV exportés. Laissé vide, c’est le nom de la source qui sert.',
    'sources.sourceNameLabel': 'Nom de la source',
    'sources.sourceNameHelp': "Pour t'y retrouver dans cette liste, quand une entreprise a plusieurs portails.",
    'sources.watchedUrlLabel': 'URL surveillée',
    'sources.urlWillRedetect': "La plateforme sera re-détectée sur cette adresse. Si elle n'est pas reconnue, rien ne sera enregistré.",
    'sources.currentPlatform': 'Plateforme actuelle : {platform}.',
    'sources.removeConfirmTitle': 'Retirer {name} de la veille ?',
    'sources.removing': 'Retrait…',
    'sources.removeFromWatch': 'Retirer de la veille',
    'sources.removeConfirmP1': 'Elle ne sera plus interrogée la nuit, et ses {n} offres sortent de la veille.',
    'sources.removeConfirmP2': "Rien n'est effacé : les scores déjà calculés, les dossiers générés et les candidatures en cours restent intacts, et la source se remet en veille en un clic. C'est ce qui permet de la retirer sans se demander ce qu'on perd.",

    'decisions.loading': 'Chargement de la pile…',
    'decisions.nothingPending': 'Rien en attente',
    'decisions.countPlural': '{n} offres',
    'decisions.countSingular': '{n} offre',
    'decisions.goodPlural': '{n} bonnes correspondances',
    'decisions.goodSingular': '{n} bonne correspondance',
    'decisions.lastRun': 'dernier run {when}',
    'decisions.searchAria': 'Rechercher dans la pile',
    'decisions.onboardTitle': "Radar n'a encore rien à vous montrer",
    'decisions.onboardIntro': "C'est normal, votre compte vient d'être créé. Deux choses à faire, une fois, et la veille tourne toutes les nuits sans que vous ayez à y penser.",
    'decisions.onboardStep1Title': 'Déposer votre CV',
    'decisions.onboardStep1Help': 'Un PDF suffit. Radar en extrait vos expériences et vos compétences, environ vingt secondes. C’est ce CV qui sert de référence à toutes les comparaisons.',
    'decisions.onboardStep1Action': 'Déposer un PDF',
    'decisions.onboardStep2Title': 'Choisir des sites à surveiller',
    'decisions.onboardStep2Help': 'Collez l’adresse d’une page carrière. Radar reconnaît la plateforme tout seul et vous montre ce qu’il arrive à lire avant que vous validiez.',
    'decisions.onboardStep2Action': 'Ajouter un site',
    'decisions.onboardNote': 'La première nuit de veille démarre dès le premier site ajouté. Vous pouvez fermer, Radar continue.',
    'decisions.pileZeroText': 'Tout ce qui est arrivé a été tranché. Les propales en cours sont dans',
    'decisions.pileZeroTracking': 'le suivi',
    'decisions.pileZeroText2': 'et tout ce qui a été capté reste consultable dans',
    'decisions.pileZeroWatch': 'la veille',
    'decisions.tierAria': 'Palier de correspondance',
    'decisions.allScores': 'Tous scores',
    'decisions.selectAll': 'Tout sélectionner',
    'decisions.selectedPlural': '{n} sélectionnées',
    'decisions.selectedSingular': '{n} sélectionnée',
    'decisions.batchLater': 'remises à plus tard',
    'decisions.later': 'Plus tard',
    'decisions.batchSkipped': 'écartées',
    'decisions.skip': 'Écarter',
    'decisions.deselect': 'Désélectionner',
    'decisions.selectHint': "Coche plusieurs offres pour les écarter d'un coup. Ouvrir une offre ne la sort pas de la pile.",
    'decisions.shownOfTotal': '{shown} sur {total}',
    'decisions.noMatch': 'Aucune offre de la pile ne correspond à ces filtres.',
    'decisions.resetShort': 'Réinitialiser',
    'decisions.selectOffer': 'Sélectionner {title}',
    'decisions.laterTitle': 'Je la reverrai plus tard',
    'decisions.reopenedToast': '« {title} » reprise, elle revient à trancher',
    'decisions.skippedTitle': 'Écartées',
    'decisions.skippedHint': "Rien n'est supprimé, vous pouvez revenir sur la décision",
    'decisions.skippedAt': 'écartée {when}',
    'decisions.reopenTitle': 'Remettre dans la pile',
    'decisions.reopen': 'Reprendre',
    'decisions.skippedToast': '« {title} » écartée',
    'decisions.laterToast': '« {title} » remise à plus tard',
    'decisions.appliedToast': '« {title} » ajoutée à vos candidatures',
    'decisions.sortButton': 'Trier ({n})',
    'decisions.thresholdLabel': 'Me montrer à partir de',
    'decisions.thresholdEffect': '{n} offres sur {total} à trier',
    'decisions.thresholdSaved': 'Seuil enregistré : {n}',
    'tri.title': 'Trier mes opportunités',
    'tri.progress': '{n} sur {total}',
    'tri.close': 'Fermer',
    'tri.doneTitle': 'Pile triée',
    'tri.doneText': '{n} offres triées. Celles que vous suivez sont dans vos candidatures.',
    'tri.noProfile': "Aucun score sur cette offre : ouvrez la fiche pour l'évaluer.",
    'decisions.batchToast': '{n} offre(s) : {action}',
    'decisions.tierGood': 'Bonne correspondance',
    'decisions.tierGoodRange': '70 et plus',
    'decisions.tierMid': 'À vérifier',
    'decisions.tierMidRange': '40 à 69',
    'decisions.tierLow': 'Correspondance faible',
    'decisions.tierLowRange': 'sous 40',

    'offre.loading': 'Chargement de l’offre…',
    'offre.notFound': 'Offre introuvable',
    'offre.statusAriaLabel': 'Décision sur cette offre',
    'offre.kindMission': 'Mission',
    'offre.kindJob': 'Emploi',
    'offre.reperee': 'Repérée le {date}',
    'offre.avisEnFrancais': 'Cet avis a été rédigé en français.',
    'offre.avisEnAnglais': 'Cet avis a été rédigé en anglais.',
    'offre.avisEnNeerlandais': 'Cet avis a été rédigé en néerlandais.',
    'offre.avisEnItalien': 'Cet avis a été rédigé en italien.',
    'offre.avisEnEspagnol': 'Cet avis a été rédigé en espagnol.',
    'offre.avisRetraduit': 'Radar le réécrit dans votre langue d’ici quelques nuits.',
    'offre.avisFaux': 'Cet avis vous semble faux ?',
    'offre.avisFauxMerci': 'Merci, c’est noté.',
    'offre.avisPour': 'Pour vous',
    'offre.avisAVerifier': 'À vérifier',
    'offre.avisAdapte': 'Avec un CV adapté : {n}',
    'offre.exigenceCouverte': 'Couvert',
    'offre.exigenceAConfirmer': 'À confirmer',
    'offre.exigenceManquante': 'Manquant',
    'offre.detailScore': 'Le détail du score',
    'offre.annonceComplete': 'L’annonce complète',
    'offre.evaluatedProfilesPlural': '{n} profils évalués',
    'offre.evaluatedProfilesSingular': '{n} profil évalué',
    'offre.originalPosting': "Annonce d'origine",
    'offre.deadlineValue': 'Échéance : {value}',
    'offre.startValue': 'Début souhaité : {value}',
    'offre.endValue': 'Fin prévue : {value}',
    'offre.notSetFem': 'non renseignée',
    'offre.notSetMasc': 'non renseigné',
    'offre.proposalsPlural': '{n} propositions déposées',
    'offre.proposalsSingular': '{n} proposition déposée',
    'offre.proposalsUnset': 'nombre de propositions non renseigné',
    'offre.frameworkUnset': 'contrat-cadre non renseigné',
    'offre.collapseDesc': 'Replier la description',
    'offre.expandDesc': 'Lire la description complète',
    'offre.noDescriptionCaptured': 'Pas de description récupérée.',
    'offre.applicationsHeader': 'Candidatures',
    'offre.proposedAgo': 'proposé {when}',
    'offre.viewFileLink': 'voir le dossier',
    'offre.candidats': 'Candidats',
    'offre.aucunCandidat': 'Aucun profil évalué pour l’instant.',
    'offre.compareName': 'Comparer {name}',
    'offre.scoreAriaLabel': '{name} : {score} sur 100',
    'offre.onDemandTitle': 'Évalué à ta demande, hors pré-filtre',
    'offre.gainAfterAdaptation': '↑ {gain} après adaptation',
    'offre.fileGeneratedTitle': 'Dossier généré',
    'offre.compareHintBase': 'Coche deux profils ou plus pour les comparer côte à côte,',
    'offre.compareOr': 'ou',
    'offre.compareBest': 'compare les {n} meilleurs',
    'offre.compareFreeSuffix': " c'est déjà gratuit et instantané, tout est en base.",
    'offre.compareMax': '{n} profils au maximum dans une comparaison',
    'offre.journal': "Journal de l'offre",
    'offre.generatingFile': 'Génération du dossier de {name}…',
    'offre.generatingHelp': "Réécriture fidèle du CV pour l'offre. Chaque modification sera auditée.",
    'offre.comparedCount': '{n} profils comparés sur cette offre',
    'offre.closeComparison': 'Fermer la comparaison',
    'offre.generateTheirFile': 'Générer son dossier',
    'offre.applyTitle': 'Vous postulez ?',
    'offre.applyBody1': "l'ajoute à vos candidatures, sans rien calculer.",
    'offre.adaptCvLabel': 'Adapter mon CV',
    'quota.restantes': '{n} adaptations restantes cette semaine, sur {max}',
    'quota.aZero': 'Plus d’adaptation cette semaine. Le compteur repart lundi.',
    'offre.applyBody2': "le réécrit pour cette offre : c'est la seule étape qui coûte quelque chose, et elle n'a lieu que si vous la demandez.",
    'offre.presentTitle': 'Prêt à présenter {name} ?',
    'offre.presentBody1': 'crée la candidature et la suit, sans rien appeler.',
    'offre.theFileLabel': 'Le dossier',
    'offre.presentBody2': "(CV réécrit pour l'offre + audit) est la seule étape coûteuse, et il n'est généré que si tu le demandes.",
    'offre.alreadyInApplications': 'déjà dans vos candidatures',
    'offre.alreadyProposed': 'déjà proposé',
    'offre.about15s': '~15 s',
    'offre.generateAdaptedFile': 'Générer le dossier adapté',
    'offre.emptyAnalysis': "Aucune évaluation pour l'instant.",
    'offre.statusSkippedToast': 'Offre écartée',
    'offre.statusLaterToast': 'Mise de côté, elle est dans le suivi',
    'offre.statusReopenedToast': 'Remise dans les décisions à prendre',
    'offre.neighboursTitle': 'Profils des catégories voisines',
    'offre.neighboursHelp': "Ils ne partagent pas la catégorie de cette offre, mais une catégorie sœur. Ils ne sont pas évalués automatiquement.",
    'offre.scoredToast': '{name} : {score}/100',
    'offre.evaluate': 'Évaluer',
    'offre.notYetEvaluated': "Cette offre n'a pas encore été évaluée pour vous",
    'offre.evaluatingLabel': 'Évaluation…',
    'offre.alreadyScoredToast': 'Déjà évalué : {score}/100',
    'offre.evaluatedToast': 'Évalué : {score}/100',
    'offre.evaluateMyProfile': 'Évaluer mon profil',
    'offre.evaluateHelp': '~3 s, un appel IA. Le résultat est conservé.',
    'offre.evaluateAnother': 'Évaluer un autre profil',
    'offre.employerUnknown': 'Employeur non précisé',
    'offre.employerViaSource': 'via {source}',
    'offre.radarSkipToast': 'Pas pour moi : elle ne reviendra pas',
    'offre.radarLaterToast': 'Gardée dans Mes offres. Radar vous la rappellera le lundi {date} si elle est toujours en ligne.',
    'offre.radarReopenToast': 'Décision annulée',
    'decisions.skippedToastRadar': '« {title} » : pas pour moi',
    'decisions.reopenedToastRadar': '« {title} » est de retour dans vos opportunités',
    'sources.colState': 'État',
    'sources.stateOk': 'OK',
    'sources.broken': 'En panne',
    'sources.brokenSince': 'En panne depuis le {date}',
    'veille.notScored': 'Non évaluée',
    'offre.appliedToast': 'Ajoutée à vos candidatures',
    'offre.descriptionTitle': 'Le poste',
    'offre.behindTheScenes': 'Comment Radar a traité cette offre',
    'offre.generatingMyCv': 'Adaptation de votre CV…',

    'config.loading': 'Chargement des réglages…',
    'config.saving': 'Enregistrement…',
    'config.savedToast': 'Réglages enregistrés',
    'config.save': 'Enregistrer',
    'config.sliderAria': 'Score minimum pour entrer dans la pile',
    'config.scaleLow': '← tout voir, y compris ce qui ne convient pas',
    'config.scaleHigh': 'ne voir que les meilleures correspondances →',
    'config.computingEffect': "Calcul de l'effet…",
    'config.thresholdReference': 'Repère : le moteur juge « non » en dessous de 40, « peut-être » jusqu\'à 68, « oui » à partir de 70.',
    'config.optimizationLabel': "Niveau d'adaptation",
    'config.optimizationBalanced': 'Équilibré',
    'config.optimizationTargeted': 'Ciblé',
    'config.toneLabel': 'Ton',
    'config.toneSober': 'Sobre',
    'config.toneDynamic': 'Dynamique',
    'config.toneExecutive': 'Exécutif',
    'config.lengthLabel': 'Longueur',
    'config.languageLabel': 'Langue',
    'config.langue.auto': "Celle de l'offre",
    'config.langue.autoEffet': "La langue de l'offre si votre CV dit que vous la parlez, sinon l'anglais.",
    'config.langue.fr': 'Français',
    'config.langue.frEffet': 'Toujours en français.',
    'config.langue.en': 'Anglais',
    'config.langue.enEffet': 'Toujours en anglais.',
    'config.length1Page': '1 page',
    'config.length2Pages': '2 pages',
    'config.lengthUnlimited': 'Sans limite',
    'config.questionsLabel': 'Questions avant de générer',
    'config.questionsOnce': 'Une fois par offre',
    'config.questionsAlways': 'À chaque génération',
    'config.niveau.fidele': "Fidèle",
    'config.niveau.audacieux': "Audacieux",
    'config.niveau.fideleEffet': "Garde toutes vos lignes, les réécrit et les réordonne pour l’offre.",
    'config.niveau.equilibreEffet': "Réécrit librement et raccourcit un peu ce qui sert moins l’offre.",
    'config.niveau.cibleEffet': "Garde ce qui sert l’offre, raccourcit le reste.",
    'config.niveau.audacieuxEffet': "Peut affirmer ce que l’offre demande et que votre CV ne dit pas.",
    'config.ton.sobreEffet': "Phrases directes, sans superlatifs.",
    'config.ton.dynamiqueEffet': "Verbes d’action, résultats en avant.",
    'config.ton.executifEffet': "Pilotage, vision, responsabilités.",
    'config.longueur.unePageEffet': "Seulement l’essentiel pour cette offre.",
    'config.longueur.deuxPagesEffet': "Concis, sans tout sacrifier.",
    'config.longueur.sansLimiteEffet': "Aucune contrainte de longueur.",
    'config.questions.aide': "Sur ce que l’offre demande et que votre CV ne dit pas, puis sur l’angle de la lettre.",
    'config.questions.uneFoisEffet': "Posées à la première génération pour une offre, puis plus.",
    'config.questions.toujoursEffet': "Reposées avant chaque CV et chaque lettre.",
    'compte.motDePasse': "Mot de passe",
    'compte.changer': "Changer",
    'compte.telecharger': "Télécharger mes données",
    'compte.supprimer': "Supprimer mon compte",
    'compte.supprimerAide': "Votre CV, vos candidatures, vos CV adaptés et vos réglages sont effacés. Les factures restent chez Stripe. C’est définitif.",
    'compte.supprimerConfirmer': "Supprimer définitivement",
    'compte.actuel': "Mot de passe actuel",
    'compte.motDePasseAide': "Au moins {n} caractères. Vos autres appareils seront déconnectés.",
    'compte.enregistrer': "Changer le mot de passe",
    'compte.actuelFaux': "Le mot de passe actuel n’est pas le bon.",
    'config.signature': 'Signature',
    'config.authorLabel': 'Votre nom',
    // Lot 2 de la refonte de Radar (accueil, inscription, rythme des mails).
    'radar.moi': "Moi",
    'radar.menuAria': "Mon compte",
    'reglages.titre': "Réglages",
    'mails.quand': "Quand",
    'marche.offres1': '1 offre comme la vôtre ce mois-ci',
    'marche.offres': '{n} offres comme la vôtre ce mois-ci',
    'marche.chez1': 'chez 1 employeur',
    'marche.chez': 'chez {e} employeurs',
    'marche.salaire': 'Salaire affiché : {min} à {max} €.',
    'sugg.employeur': "{n} refus « Pas cet employeur » pour {nom}.",
    'sugg.employeurAction': "Rendre {nom} muet",
    'sugg.regions': "{n} refus « Trop loin ».",
    'sugg.regionsAction': "Choisir vos régions",
    'sugg.salaire': "{n} refus pour le salaire.",
    'sugg.salaireAction': "Poser un minimum",
    'sugg.metier': "{n} refus « Pas mon métier ».",
    'sugg.metierAction': "Revoir vos métiers",
    'sugg.seuil': "Rien n’a passé votre seuil depuis une semaine. Vos meilleures offres sont à {n}.",
    'sugg.seuilAction': "Passer à {seuil}",
    'sugg.seuilFait': "Seuil passé à {seuil}.",
    'cherche.placeholder': "Chercher dans toutes les offres suivies",
    'cherche.rien': "Aucune offre.",
    'tri.ouvrir': "Tri rapide",
    'tri.titre': "Tri rapide · {n} / {total}",
    'tri.voir': "Voir",
    'tri.plusTard': "Plus tard",
    'tri.pasPourMoi': "Pas pour moi",
    'tri.passer': "Suivante sans décider",
    'tri.fermer': "Fermer",
    'tri.fini': "Tout est trié.",
    'tri.plusTardOk': "Gardée pour plus tard.",
    'tri.pasPourMoiOk': "Écartée.",
    'excl.cdd': "CDD",
    'excl.interim': "Intérim",
    'excl.freelance': "Freelance",
    'excl.stage': "Stage",
    'excl.junior': "Junior",
    'excl.senior': "Senior",
    'excl.langues': "Langues exigées que je ne parle pas",
    'excl.salaire': "Salaire brut annuel minimum",
    'excl.contrats': "Contrats acceptés",
    'excl.cdi': "CDI",
    'excl.confirme': "Confirmé",
    'excl.niveaux': "Niveaux qui me conviennent",
    'excl.teletravail': "Télétravail",
    'excl.temps': "Temps de travail",
    'excl.tempsPlein': "Temps plein",
    'excl.tempsPartiel': "Temps partiel ou 4/5",
    'excl.peuImporte': "Peu importe",
    'excl.hybride': "Au moins hybride",
    'excl.salaireAucun': "Aucun",
    'excl.salaireAutre': "Autre…",
    'excl.k': "{n} k€",
    'excl.parAn': "€ brut par an",
    'excl.jeParle': "Je parle",
    'excl.parle.fr': "Français",
    'excl.parle.nl': "Néerlandais",
    'excl.parle.en': "Anglais",
    'excl.parle.de': "Allemand",
    'reglages.cherche.titre': "Ce que je cherche",
    'reglages.cherche.aide': "Vaut pour les mails et l’accueil. Une offre qui ne dit rien sur un point passe toujours.",
    'reglages.regions': "Où",
    'reglages.ecrit.titre': "Quand Radar m’écrit",
    'reglages.avance': "Avancé",
    'reglages.compte.titre': "Mon compte",
    'reglages.mails.coupesIci': "Les mails sont coupés. Ils se rallument dans « Mon compte ».",
    'mails.matin': "Le matin même",
    'mails.hebdo': "Le lundi seulement",
    'mails.quels': "Quels mails",
    'mails.mots': "Mots suivis",
    'mails.motsAide': "Une nouvelle offre qui contient l’un d’eux part par mail, quel que soit son score. Dix au plus, séparés par des virgules.",
    'mails.motsExemple': "Kubernetes, product owner",
    'mails.lundi': "Récap du lundi",
    'mails.relance': "Relance à J+7",
    'mails.accueil': "Seuil de l’accueil",
    'mails.commeMails': "Comme les mails ({n})",
    'mails.saved': "Enregistré.",
    'mails.prioritaire': "Prioritaire",
    'mails.muet': "Muet",
    'mails.prioritaireAide': "Toute offre dans vos métiers part par mail.",
    'mails.muetAide': "Plus aucun mail pour cet employeur.",
    'reglages.mails.coupes': "Vous ne recevez plus les mails de Radar. Il continue de veiller : vos offres restent ici.",
    'reglages.mails.reactiver': "Recevoir à nouveau les mails",
    'reglages.mails.couper': "Ne plus recevoir de mails",
    'rythme.aria': "Rythme des mails",
    'rythme.discret.nom': "Discret",
    'rythme.discret.seuil': "80 et plus",
    'rythme.discret.desc': "Seulement les offres très proches de votre CV.",
    'rythme.equilibre.nom': "Équilibré",
    'rythme.equilibre.seuil': "70 et plus",
    'rythme.equilibre.desc': "Les offres proches de votre CV.",
    'rythme.actif.nom': "Je cherche activement",
    'rythme.actif.seuil': "60 et plus",
    'rythme.actif.desc': "Aussi les offres un peu plus éloignées.",
    'rythme.estimationZero': "Aucune offre ces 30 derniers jours",
    'rythme.estimationUn': "1 offre ces 30 derniers jours",
    'rythme.estimationPlus': "{n} offres ces 30 derniers jours",
    'rythme.note': "Compté sur vos employeurs ces 30 derniers jours. Une nuit, un mail au plus.",
    'rythme.perso': "Votre seuil actuel, {n}, a été posé à la main. Choisir un rythme le remplace.",
    'accueil2.sansCv': "Radar compare chaque offre à votre CV. Sans lui, il n’a rien à vous montrer.",
    'accueil2.sansCvAction': "Déposer mon CV",
    'accueil2.titreNouvelleUn': "Une nouvelle offre passe votre seuil de {seuil}.",
    'accueil2.titreNouvellesPlus': "{n} nouvelles offres passent votre seuil de {seuil}.",
    'accueil2.titreAttenteUn': "Une offre au-dessus de {seuil} vous attend.",
    'accueil2.titreAttentePlus': "{n} offres au-dessus de {seuil} vous attendent.",
    'accueil2.titreCalme': "Rien de nouveau au-dessus de {seuil}.",
    'accueil2.aucunSuivi': "Vous ne suivez encore aucun employeur.",
    'accueil2.aFaire': "À faire",
    'accueil2.finFile': "C’est tout pour aujourd’hui.",
    'file.aria': "Offres à trier",
    'file.position': "Offre {rang} sur {total}",
    'file.suivante': "Suivante",
    'file.terminer': "Terminer",
    'accueil2.etat': "Radar a relu vos sites {quand}.",
    'accueil2.etatPremiere': "Première relecture de vos sites cette nuit.",
    'accueil2.lundi': "Récap lundi, même sans nouvelle.",
    'accueil2.mailsCoupes': "Vous ne recevez pas de mails.",
    'accueil2.mailsReactiver': "Les réactiver",
    'accueil2.pause': "Mails en pause jusqu’au {date}",
    'accueil2.reprendre': "Reprendre maintenant",
    'pause.titre': "Faire une pause",
    'pause.uneSemaine': "1 semaine",
    'pause.semaines': "{n} semaines",
    'pause.jusqua': "En pause jusqu’au {date}",
    'accueil2.pourVous': "Pour vous",
    'accueil2.pourVousPlus': "Les {n} meilleures sur {total}.",
    'accueil2.nouvelle': "Nouvelle",
    'accueil2.via': "via {site}",
    'accueil2.atoutsLabel': "Pour vous :",
    'accueil2.manquesLabel': "À vérifier :",
    'accueil2.tranchee': "{etat} : {titre}",
    'accueil2.dessousTitre': "Juste en dessous",
    'accueil2.plusProches': "Les plus proches",
    'accueil2.dessousUn': "1 offre entre {min} et {max} cette semaine",
    'accueil2.dessousPlus': "{n} offres entre {min} et {max} cette semaine",
    'accueil2.dessousNote': "Elles ne vous ont pas été envoyées par mail.",
    'accueil2.changerRythme': "Changer mon rythme",
    'accueil2.choisirEmployeurs': "Choisir mes employeurs",
    'accueil2.empErreur': "Illisible en ce moment. L’équipe est prévenue, rien à faire de votre côté.",
    'accueil2.empAnnonces': "Site d’annonces : {n} offres dans vos métiers, la meilleure à {m}",
    'accueil2.empAnnoncesRien': "Site d’annonces : rien dans vos métiers pour l’instant",
    'accueil2.empAnnoncesUn': "Site d’annonces : 1 offre dans vos métiers, à {m}",
    'accueil2.empRecrutePlus': "Recrute dans vos métiers : {n} offres, la meilleure à {m}",
    'accueil2.empRecruteUn': "Recrute dans vos métiers : 1 offre, à {m}",
    'accueil2.empCalme': "Calme dans vos métiers.",
    'accueil2.enCoursUn': "1 candidature en cours",
    'accueil2.enCoursPlus': "{n} candidatures en cours",
    'bienvenue.etape': "Étape {n} sur 3",
    'bienvenue.cv.titre': "D’abord, votre CV",
    'bienvenue.cv.sous': "Radar le lit une fois, puis s’en sert pour comparer chaque offre. Il reste privé.",
    'bienvenue.essai': 'Radar est gratuit pendant un mois, sans carte.',
    'bienvenue.cv.extension': 'Votre extension a déjà votre CV.',
    'bienvenue.cv.reprendre': 'Le reprendre',
    'bienvenue.cv.reprendreEchec': 'Le CV de l’extension n’a pas pu être lu.',
    'ext.installee': 'Votre extension est installée.',
    'ext.connecter': 'La connecter',
    'ext.connectee': 'Extension connectée.',
    'ext.echec': 'La connexion n’a pas abouti. Réessayez.',
    'ext.echecSansCv': 'Déposez d’abord votre CV dans Radar.',
    'bienvenue.cv.depot': "Glissez votre CV en PDF ici",
    'bienvenue.cv.ou': "ou touchez pour choisir un fichier",
    'bienvenue.cv.lecture': "Radar lit votre CV, une vingtaine de secondes…",
    'bienvenue.cv.lu': "Votre CV est lu.",
    'bienvenue.cv.luNom': "Votre CV est lu : {nom}.",
    'bienvenue.cv.metiers': "Radar cherchera des postes de",
    'bienvenue.cv.metiersAide': "Retirez ce qui ne vous concerne pas, ajoutez ce qui manque. Radar n’évalue que les offres de ces métiers.",
    'bienvenue.cv.suivant': "C’est bien moi",
    'bienvenue.emp.titre': "Chez qui diriez-vous oui demain ?",
    'bienvenue.emp.sous': "Choisissez jusqu’à {max} sites. Radar relit leurs offres chaque nuit. Ils sont triés selon vos métiers.",
    'bienvenue.emp.sousSansMax': "Radar relit leurs offres chaque nuit. Ils sont triés selon vos métiers.",
    'bienvenue.emp.dansMetiers': "{n} dans vos métiers",
    'bienvenue.emp.rien': "Rien dans vos métiers aujourd’hui",
    'bienvenue.emp.annoncesTitre': "Sites d’annonces",
    'bienvenue.emp.annoncesAide': "Ils publient pour des centaines d’employeurs, et Radar n’en garde que vos métiers. Cochés d’office : c’est d’eux que viennent la plupart des bonnes offres.",
    'bienvenue.emp.employeursTitre': "Employeurs",
    'bienvenue.emp.manque': "Votre employeur n’est pas là ? Ajoutez l’adresse de sa page carrières depuis l’onglet Employeurs, une fois votre radar en place.",
    'bienvenue.continuer': "Continuer",
    'bienvenue.retour': "Retour",
    'bienvenue.rythme.titre': "Quand voulez-vous être dérangé ?",
    'bienvenue.rythme.pasEncore': "Radar compare en ce moment les offres de vos sites à votre CV. Vous verrez combien chaque rythme en envoie dans vos réglages, dès demain.",
    'bienvenue.fin': "Mettre mon radar en place",
    'offre.vpQuestion': "Vous postulez ?",
    'offre.vpOui': "Oui, préparer mon CV",
    'offre.vpCvPret': "Votre CV est prêt.",
    'offre.vpSansAdapter': "Je postule sans adapter mon CV",
    'suivi.aDecider': "Vos offres gardées",
    'suivi.aDeciderAide': "Gardées depuis l’extension ou ajoutées à la main.",
    'suivi.gardeeLe': "Gardée le {date}",
    'regions.question': "Où acceptez-vous de travailler ?",
    'regions.aide': "Rien de coché : partout. Une offre ailleurs n’est jamais envoyée par mail, mais reste visible chez son employeur. Une offre sans lieu connu passe toujours.",
    'regions.aria': "Régions",
    'regions.bruxelles': "Bruxelles",
    'regions.wallonie': "Wallonie",
    'regions.flandre': "Flandre",
    'regions.luxembourg': "Luxembourg",
    'regions.etranger': "Hors Belgique et Luxembourg",
    'filtre.titre': "Que suivre chez {nom} ?",
    'filtre.aide': "{n} offres en ligne. Cochez au moins une zone ou une langue.",
    'filtre.aidePetit': "{n} offres en ligne. Rien de coché : tout.",
    'filtre.aideTraduite': "{n} offres, publiées chacune en plusieurs langues. Choisissez-en une.",
    'filtre.lecture': "Radar lit {n} offres par nuit, les plus récentes d’abord.",
    'emp.enLecture': "Lecture en cours : les plus récentes d’abord.",
    'filtre.zones': "Où",
    'filtre.langues': "Langue de l’annonce",
    'filtre.suivre': "Suivre",
    'filtre.enregistrer': "Enregistrer",
    'filtre.enregistre': "Filtre enregistré.",
    'filtre.changer': "Changer le filtre",
    'filtre.vide': "Aucune zone ni langue lisible sur ce site.",
    'zones.brabant-wallon': "Brabant wallon",
    'zones.hainaut': "Hainaut",
    'zones.liege': "Liège",
    'zones.luxembourg-be': "Province de Luxembourg",
    'zones.namur': "Namur",
    'zones.anvers': "Anvers",
    'zones.brabant-flamand': "Brabant flamand",
    'zones.flandre-occidentale': "Flandre occidentale",
    'zones.flandre-orientale': "Flandre orientale",
    'zones.limbourg': "Limbourg",
    'zones.france': "France",
    'zones.pays-bas': "Pays-Bas",
    'zones.allemagne': "Allemagne",
    'zones.suisse': "Suisse",
    'zones.autre-pays': "Autres pays",
    'langues.fr': "Français",
    'langues.nl': "Néerlandais",
    'langues.en': "Anglais",
    'langues.de': "Allemand",
    'accueil2.horsRegionsUn': "1 autre offre au-dessus de {seuil} est hors de vos régions.",
    'accueil2.horsRegionsPlus': "{n} autres offres au-dessus de {seuil} sont hors de vos régions.",
    'accueil2.exclueUn': "1 offre écartée par vos critères.",
    'accueil2.excluesPlus': "{n} offres écartées par vos critères.",
    'accueil2.changerCriteres': "Vos critères",
    'accueil2.changerRegions': "Changer mes régions",
    'emp.calmeDepuis': "Calme dans vos métiers. Dernière offre il y a {n} semaines.",
    'raison.question': "Pourquoi ? (facultatif)",
    'aide.code.bouton': "Afficher un code de connexion",
    'aide.code.aide': "Dans l’extension, Options, « Me connecter avec un code Radar » : tapez ce code. Pas de mot de passe à retaper. Valable 10 minutes, une seule fois.",
    'aide.code.aria': "Code de connexion de l’extension",
    'aide.pont.ok': "Extension connectée. Vous pouvez fermer cet onglet.",
    'aide.pont.echec': "L’extension n’a pas pu se connecter. Réessayez depuis l’extension.",
    "aideR.intro": "L'extension, et quoi faire quand ça coince.",
    "aideR.ext.titre": "L'extension",
    "aideR.ext.lead": "Votre score sur chaque offre, LinkedIn compris.",
    "aideR.ext.telephone": "Elle s'installe sur ordinateur, dans Chrome. Ouvrez Radar sur votre ordinateur, puis « Aide et extension ».",
    "aideR.ext.dejaInstallee": "Déjà installée ? La connecter",
    "aideR.ext.pasInstallee": "Pas encore installée ? L'installer",
    "aideR.ext.pasDeBouton": "Pas de bouton « Connecter avec Radar » ? Utilisez un code :",
    "aideR.ext.connectee": "Extension connectée",
    "aideR.ext.nonConnectee": "L'extension n'est pas connectée",
    "aideR.ext.rienEnvoye": "Aucune offre envoyée par elle pour l'instant.",
    "aideR.ext.dernierEnvoi": "Dernier envoi : {date}.",
    "aideR.ext.ailleurs": "Sur LinkedIn et Indeed, elle s'active seule. Ailleurs, montrez-lui le site une fois :",
    "demo.pause": "Pause",
    "demo.lecture": "Lecture",
    "demo.reduit": "Animation coupée : « réduire les animations » est activé.",
    "demo.annuler": "Annuler",
    "demo.refuser": "Refuser",
    "demo.autoriser": "Autoriser",
    "demo.install.titre": "Installer et épingler",
    "demo.install.aria": "Animation : sur le Chrome Web Store, cliquer « Ajouter à Chrome », confirmer, puis ouvrir la pièce de puzzle et épingler l'extension. Son icône apparaît dans la barre.",
    "demo.install.e1": "« Ajouter à Chrome »",
    "demo.install.e2": "« Ajouter l'extension »",
    "demo.install.e3": "La pièce de puzzle, puis l'épingle",
    "demo.install.slogan": "Votre score sur chaque offre",
    "demo.install.dlgTitre": "Ajouter « AI Job Fit » ?",
    "demo.install.dlgTexte": "Elle pourra lire les offres que vous ouvrez.",
    "demo.install.menu": "Extensions",
    "demo.install.ajouter": "Ajouter l'extension",
    "demo.ac.bouton": "Activer sur ce site",
    "demo.ac.emp": "CHU Exemple · Liège",
    "demo.co.titre": "Connecter à Radar",
    "demo.co.aria": "Animation : dans les options de l'extension, cliquer « Vous avez un compte Radar ? », puis « Connecter avec Radar », autoriser. Radar confirme dans un onglet, et le CV arrive dans l'extension.",
    "demo.co.e1": "« Vous avez un compte Radar ? »",
    "demo.co.e2": "« Connecter avec Radar », puis « Autoriser »",
    "demo.co.e3": "Radar confirme dans un onglet",
    "demo.co.e4": "Votre CV est dans l'extension",
    "demo.co.import": "Importez votre CV pour commencer",
    "demo.co.depot": "Déposez votre CV",
    "demo.co.lien": "Vous avez un compte Radar ? Connectez-vous",
    "demo.co.compte": "J'ai un compte Radar",
    "demo.co.bouton": "Connecter avec Radar",
    "demo.co.ouBien": "Ou bien : mot de passe, ou code Radar",
    "demo.co.perm": "Autoriser l'accès à Radar ?",
    "demo.co.fermer": "Vous pouvez fermer cet onglet.",
    "demo.co.cv": "Votre CV vient de Radar",
    "demo.co.nom": "Votre nom",
    "demo.co.metier": "Votre métier",
    "demo.co.bouton2": "« Enregistrer dans Radar » apparaît sur chaque offre.",
    "demo.ac.titre": "Activer sur un site inconnu",
    "demo.ac.aria": "Animation : sur l'annonce d'un site d'employeur, cliquer l'icône de l'extension, puis « Activer sur ce site », autoriser. Le score s'affiche sur l'annonce.",
    "demo.ac.e1": "L'icône de l'extension",
    "demo.ac.e2": "« Activer sur ce site »",
    "demo.ac.e3": "« Autoriser », une fois par site",
    "demo.ac.e4": "Le score s'affiche sur l'annonce",
    "demo.ac.poste": "Analyste financier (H/F/X)",
    "demo.ac.pasAcces": "Pas encore active sur ce site.",
    "demo.ac.perm": "Autoriser sur ce site ?",
    "demo.ac.profil": "Bon profil",
    "demo.ac.adapter": "Adapter mon CV",
    "demo.ac.enregistrer": "Enregistrer dans Radar",
    "demo.emp.titre": "Un employeur pas encore suivi",
    "demo.emp.aria": "Animation : vous demandez l'activation, un mail confirme tout de suite, un second mail dit que l'employeur est en ligne, en général sous une semaine.",
    "demo.emp.l1": "Vous demandez",
    "demo.emp.l1s": "« Demander l'activation »",
    "demo.emp.l2": "Un mail confirme",
    "demo.emp.l2s": "tout de suite",
    "demo.emp.l3": "C'est en ligne",
    "demo.emp.l3s": "en général sous une semaine",
    "aideR.faq.titre": "Quand ça coince",
    "aideR.q.score": "Pas de score sur une annonce",
    "aideR.r.score": "Sur un site qu'elle ne connaît pas, l'extension attend votre accord. Une fois par site :",
    "aideR.r.scoreTel": "Sur un site qu'elle ne connaît pas, l'extension attend votre accord : cliquez son icône, puis « Activer sur ce site ». Une fois par site.",
    "aideR.q.reconnait": "L'extension ne me reconnaît plus",
    "aideR.r.reconnait": "Nouvel ordinateur, mot de passe changé : reconnectez-la. Dans ses options, « Connecter avec Radar », ou tapez ce code :",
    "aideR.q.employeur": "Un employeur n'est pas dans la liste",
    "aideR.r.employeur": "Ajoutez l'adresse de sa page carrières dans Employeurs. Si Radar ne sait pas encore la lire, demandez l'activation. Ce n'est pas une panne.",
    "aideR.cta.employeur": "Aller aux employeurs",
    "aideR.q.mails": "Je ne reçois pas les mails",
    "aideR.r.mails": "Regardez d'abord dans les indésirables. Une adresse d'entreprise filtre parfois Radar : essayez une adresse personnelle.",
    "aideR.cta.adresse": "Changer d'adresse",
    "aideR.q.rythme": "Trop de mails, ou pas assez",
    "aideR.r.rythme": "Changez de rythme. Chacun dit combien d'offres il vous aurait envoyées.",
    "aideR.cta.rythme": "Changer de rythme",
    "aideR.q.pause": "Je pars en vacances",
    "aideR.r.pause": "Mettez les mails en pause une, deux ou quatre semaines. Ils reprennent seuls.",
    "aideR.cta.pause": "Mettre en pause",
    "aideR.q.supprimer": "Supprimer mon compte",
    "aideR.r.supprimer": "En bas des Réglages. Votre CV et vos scores sont effacés. Vous pouvez d'abord télécharger vos données.",
    "aideR.cta.compte": "Ouvrir mon compte",
    "aideR.prive.titre": "Ce qui reste privé",
    "aideR.prive.texte": "Votre CV, vos scores et vos candidatures ne sont visibles que de vous. Les employeurs suivis et leurs offres sont communs à tous les comptes.",
    "aideR.ecrire.titre": "Nous écrire",
    "aideR.ecrire.texte": "Un bug, une idée, un site à ajouter : le message arrive par mail, avec votre adresse pour la réponse.",
    'manques.titre': "Ce que vos offres demandent et que votre CV ne dit pas",
    'manques.intro': "Sur les {n} offres de vos métiers ces 30 derniers jours. Si vous l’avez, écrivez-le : vos prochains scores en tiendront compte.",
    'manques.chez': "demandé chez {liste}",
    'manques.sitesAnnonces': "des sites d’annonces",
    'manques.jeLai': "Je l’ai",
    'manques.ajoute': "« {mot} » ajouté à votre CV, dans vos compétences.",
    'manques.annuler': "Annuler",
    'manques.retire': "« {mot} » retiré de votre CV.",
    'manques.nAria': "Demandé par {n} offres",
    'adresse.actuelle': "Radar vous écrit à {email}.",
    'adresse.changer': "Utiliser une autre adresse",
    'adresse.placeholder': "Votre adresse personnelle",
    'adresse.envoyer': "Envoyer le lien de confirmation",
    'adresse.aide': "Une adresse professionnelle ? Vos alertes d’emploi seraient lues sur le serveur de votre employeur. Une autre adresse se confirme par un lien.",
    'adresse.attente': "En attente de confirmation : {attente}.",
    'adresse.statut.lienLe': "Lien envoyé : {quand}.",
    'adresse.statut.envoye': "Brevo l’a pris en charge, pas encore de nouvelles du serveur d’en face.",
    'adresse.statut.livre': "Accepté par le serveur de cette adresse.",
    'adresse.statut.differe': "Retardé par le serveur de cette adresse, nouvel essai en cours.",
    'adresse.statut.refuse': "Refusé par le serveur de cette adresse : {raison}",
    'adresse.statut.pasParti': "Le lien n’est pas parti : {erreur}",
    'adresse.statut.pasRecu': "Pas reçu ? Regardez les indésirables ou la quarantaine de votre messagerie.",
    'adresse.renvoyer': "Renvoyer le lien",
    'adresse.lienToast': "Lien envoyé à {email}. Radar y écrira dès qu’il sera ouvert.",
    'adresse.compteToast': "Radar vous écrit de nouveau à l’adresse de votre compte.",
    'adresse.echecToast': "Le lien n’a pas pu partir. Réessayez plus tard.",
    'adresse.confirmee': "Adresse confirmée : Radar vous écrira désormais là.",
    'adresse.lienInvalide': "Ce lien de confirmation n’est plus valable. Redemandez-en un.",
    'parcours.aria': "Votre candidature, étape par étape",
    'parcours.cv': "Votre CV pour {employeur}",
    'parcours.cvSans': "Votre CV adapté",
    'parcours.cvPret': "Prêt. Voir et modifier ci-dessous.",
    'parcours.cvPretEstime': "Prêt. Score estimé avec ce CV : {estime} au lieu de {score}.",
    'parcours.lettre': "Votre lettre (facultatif)",
    'parcours.lettreRediger': "Rédiger la lettre",
    'parcours.lettreNon': "Je n’en ai pas besoin",
    'parcours.postuler': "Postuler chez {employeur}",
    'parcours.postulerSans': "Postuler sur l'annonce",
    'parcours.ouvrirPage': "Ouvrir la page de candidature",
    'parcours.dejaFait': "C’est fait",
    'parcours.envoye': "C’est envoyé ?",
    'parcours.envoyeOui': "Oui, c’est envoyé",
    'parcours.envoyeNon': "Je ne l’enverrai pas",
    'parcours.envoyeToast': "Noté : envoyée.",
    'parcours.envoyeNonToast': "Noté : elle quitte vos offres.",
    'parcours.envoyeLe': "Envoyée le {date}. Sans nouvelles d’ici le {relance}, Radar vous proposera une relance.",
    'suivi.lienPlaceholder': "Coller le lien d’une annonce",
    'suivi.lienAria': "Lien d’une annonce",
    'suivi.lienAjouter': "Ajouter",
    'suivi.lienLecture': "Lecture de la page…",
    'suivi.lienIllisible': "{raison} Collez son texte ci-dessous.",
    'offre.lienLater': "Vous avez choisi « Plus tard » dans le mail : confirmez d’un clic.",
    'offre.lienSkip': "Vous avez choisi « Pas pour moi » dans le mail : confirmez d’un clic.",
    'raison.loin': "Trop loin",
    'raison.metier': "Pas mon métier",
    'raison.salaire': "Salaire",
    'raison.employeur': "Pas cet employeur",
    'raison.autre': "Autre",
    'raison.merci': "Merci, c’est noté.",
    'suivi.rappelLe': "rappel le lundi {date}",
    'emp.pourVousUn': "1 offre pour vous sur {n} dans vos métiers, la meilleure à {m}.",
    'emp.pourVousPlus': "{k} offres pour vous sur {n} dans vos métiers, la meilleure à {m}.",
    'emp.uneSeule': "1 offre dans vos métiers, à {m} : elle passe votre seuil.",
    'emp.sousSeuil': "{n} offres dans vos métiers, aucune à {seuil} ou plus. La meilleure : {m}.",
    'emp.sousSeuilUn': "1 offre dans vos métiers, à {m} : sous votre seuil de {seuil}.",
    'emp.prefixeAnnonces': "Site d’annonces · {phrase}",
    'emp.retour': "Employeurs",
    'emp.introuvable': "Cet employeur n’est plus suivi par Radar.",
    'emp.suiviDepuis': "suivi depuis le {date}",
    'emp.dansMetiersTitre': "Dans vos métiers",
    'emp.etatCandidature': "Dans vos candidatures",
    'emp.autresUn': "1 autre offre chez {nom}",
    'emp.autresPlus': "{n} autres offres chez {nom}",
    'emp.chercher': "Chercher dans ces offres",
    'emp.evaluer': "Évaluer pour moi",
    'emp.aucunResultat': "Aucune offre ne correspond.",
    'emp.autresAide': "Hors de vos métiers, Radar ne note pas : il les liste pour que rien ne vous échappe. « Évaluer pour moi » compare une offre précise à votre CV.",
    'emp.vide': "Aucune offre en ligne chez cet employeur pour l’instant.",
    'emp.illisibleNuits': "Illisible depuis {n} nuits. L’équipe est prévenue, rien à faire.",
    'emp.illisibleNuit': "Illisible cette nuit. L’équipe est prévenue, rien à faire.",
    'emp.apprentissage': "En apprentissage : réponse avant le {date}. Ne compte pas dans vos {max}.",
    'emp.apprentissageDepuis': "En apprentissage depuis le {date}. Ne compte pas dans vos {max}.",
    'emp.apprendLire': "Radar apprend à lire {hote}. Réponse avant le {date}.",
    'emp.apprendLireDepuis': "Radar apprend à lire {hote} depuis le {date}.",
    'emp.refusee': "Radar ne sait pas lire {hote}.",
    'emp.rangeesIci': "En attendant, vos offres {nom} gardées depuis l’extension se rangent ici.",
    'emp.rangeesIciRefus': "Vos offres {nom} gardées depuis l’extension restent rangées ici.",
    'emp.suivisTitre': "Suivis",
    'emp.enAttenteTitre': "En apprentissage · ne comptent pas dans vos {max}",
    'emp.enAttenteTitreSansMax': 'En apprentissage',
    'emp.annoncesTitre': "Sites d’annonces",
    'emp.annoncesAide': "Des centaines d’employeurs : Radar n’en garde que vos métiers et vos régions.",
    'emp.suggeresTitre': "Suggérés pour vous",
    'emp.suggereUn': "1 offre dans vos métiers",
    'emp.suggerePlus': "{n} offres dans vos métiers",
    'emp.luQuand': "Lu {quand}",
    'emp.sousTitre': "Radar relit leurs pages chaque nuit · {n} sites suivis sur {max}",
    'emp.sousTitreSansMax': 'Radar relit leurs pages chaque nuit · {n} sites suivis',
    'pont.texte': "Vous avez gardé {n} offres chez {nom} depuis l’extension. Mettre {nom} sur votre radar ?",
    'pont.suivre': "Suivre {nom}",
    'pont.ajouter': "Ajouter sa page carrières",
    'pont.non': "Non merci",
    'pont.nonToast': "Radar ne vous le proposera plus.",
    'entretien.titre': "Préparer l’entretien",
    'entretien.aide': "Les questions probables, et ce que votre CV permet d’y répondre.",
    'entretien.preparer': "Préparer",
    'entretien.redaction': "Préparation…",
    'entretien.refaire': "Refaire",
    'entretien.aPoser': "À leur demander",
    'entretien.aideSourcee': "Radar cherche l’actualité de l’employeur et sa façon de recruter, puis prépare les questions probables. Chaque source est citée. Une minute environ.",
    'entretien.enCours': "Radar cherche, une minute environ. Vous pouvez quitter la page.",
    'entretien.erreur': "La recherche n’a pas abouti.",
    'entretien.actualite': "L’employeur en ce moment",
    'entretien.deroule': "Comment il recrute",
    'entretien.questions': "Les questions probables",
    'entretien.tireeOffre': "Tirée de l’offre :",
    'entretien.rapportee': "Rapportée",
    'entretien.rienTrouve': "Rien de public et de récent trouvé sur l’employeur.",
    'entretien.agence': "L’offre ne nomme pas l’employeur. Si vous le connaissez, Radar cherchera sur lui.",
    'entretien.employeurNom': "Nom de l’employeur",
    'entretien.avecEmployeur': "Chercher sur cet employeur",
    'entretien.parcours': "Candidature envoyée · Revoir le parcours",
    'remu.titre': "Rémunération",
    'remu.encore': "Encore {n} ce mois-ci.",
    'remu.plusDEssai': "Plus d’essai ce mois-ci.",
    'remu.aVolonte': "À volonté avec Complet.",
    'remu.avecComplet': "Avec Radar Complet.",
    'remu.comparer': "Comparer",
    'remu.nonAnnonce': "Salaire non annoncé",
    'remu.estimer': "Estimer",
    'remu.aide': "Radar cherche ce que l’offre annonce, le minimum du secteur et des repères récents, et cite chaque source. Une minute environ.",
    'remu.lancer': "Estimer le salaire",
    'remu.enCours': "Radar cherche, une minute environ. Vous pouvez quitter la page.",
    'remu.erreur': "La recherche n’a pas abouti.",
    'remu.reessayer': "Réessayer",
    'remu.offreDit': "Ce que l’offre dit",
    'remu.offreMuette': "L’offre n’annonce pas de montant.",
    'remu.minimum': "Le minimum du secteur",
    'remu.auMoins': "{qui} : au moins {montant}",
    'remu.sousEnPartie': "Le bas de la fourchette annoncée est sous ce minimum.",
    'remu.sousEntierement': "Le montant annoncé est sous ce minimum.",
    'remu.marche': "Les repères du marché",
    'remu.fourchette': "{fourchette}, d’après {n} sources qui s’accordent.",
    'remu.pasDeFourchette': "Les repères ne s’accordent pas assez pour une fourchette.",
    'remu.aucunRepere': "Aucun repère fiable trouvé.",
    'remu.unRepere': "Un seul repère fiable, pas de fourchette.",
    'remu.quatreCinquiemes': "En 4/5 : {fourchette}",
    'remu.avantages': "Les avantages",
    'remu.aDemander': "À demander",
    'remu.agence': "L’offre ne nomme pas l’employeur : les repères valent pour le métier.",
    'remu.source': "Source : {qui}",
    'remu.sourceDatee': "Source : {qui}, {date}",
    'remu.sourceOffre': "l’offre",
    'remu.sources': "Sources : {retenues}, {ecartees}",
    'remu.retenue1': "1 retenue",
    'remu.retenues': "{n} retenues",
    'remu.ecartee1': "1 écartée",
    'remu.ecarteesN': "{n} écartées",
    'remu.ecartees': "Écartées",
    'remu.estimeLe': "Estimé le {date}.",
    'remu.refaire': "Refaire",
    'remu.environ': "Environ {montant}",
    'remu.entre': "{a} à {b}",
    'remu.parHeure': "brut par heure",
    'remu.parMois': "brut par mois",
    'remu.parAn': "brut par an",
    'remu.raison.page_fermee': "page fermée ou retirée",
    'remu.raison.page_illisible': "page illisible",
    'remu.raison.introuvable': "citation introuvable sur la page",
    'remu.raison.trop_ancienne': "plus de 18 mois",
    'remu.raison.domaine_ecarte': "agrégateur ou copie",
    'remu.raison.sans_extrait': "sans citation",
    'remu.raison.sans_source': "sans adresse",
    'remu.raison.chiffre_absent': "chiffre absent de la citation",
    'remu.raison.autre_pays': "autre pays ou autre devise",
    'remu.cadre': "Pays du poste : {pays}. Montants en brut annuel.",
    'remu.fiabiliteTitre': "L’employeur",
    'remu.fiabilite': "{type}. Fiabilité des chiffres publics : {fiab}.",
    'remu.soit': "(soit {montant})",
    'remu.comp.base': "Base",
    'remu.comp.variable': "Variable",
    'remu.comp.avantage': "Avantage",
    'remu.fiab.haute': "haute",
    'remu.fiab.moyenne': "moyenne",
    'remu.fiab.basse': "basse",
    'remu.fiab.inconnue': "inconnue",
    'remu.type.grand_groupe_tech': "Grand groupe technologique",
    'remu.type.startup_croissance': "Start-up en croissance",
    'remu.type.startup_debut': "Jeune start-up",
    'remu.type.entreprise_traditionnelle': "Entreprise établie",
    'remu.type.conseil_ou_agence': "Conseil ou agence",
    'remu.type.pme_locale': "PME locale",
    'remu.type.commission': "Rémunération à la commission",
    'remu.type.recrutement_tiers': "Offre publiée par un recruteur",
    'remu.type.public': "Secteur public ou associatif",
    'remu.type.inconnu': "Type d’employeur inconnu",
    'relance.titre': "Une semaine sans nouvelles ?",
    'relance.aide': "Envoyée le {date}. Radar ne propose qu’une relance par candidature.",
    'relance.preparer': "Préparer ma relance",
    'relance.redaction': "Rédaction…",
    'relance.objet': "Objet : {sujet}",
    'relance.copier': "Copier",
    'relance.copie': "Relance copiée.",
    'relance.envoyee': "Je l’ai envoyée",
    'relance.reecrire': "Réécrire",
    'relance.reponse': "J’ai eu une réponse",
    'relance.reponseQuestion': "Quelle réponse ?",
    'relance.entretien': "Un entretien",
    'relance.refus': "Un refus",
    'relance.abandon': "Je laisse tomber",
    'relance.abandonQuestion': "Laisser tomber cette candidature ? Elle quitte Mes offres.",
    'relance.abandonOui': "Oui, la laisser tomber",
    'relance.abandonToast': "Candidature retirée de Mes offres.",
  },
  en: {
    'acces.fin.titre': 'Your Radar access has ended.',
    'acces.essai.fin': 'Your free trial ends on {date}.',
    'acces.essai.choisir': 'Choose a plan',
    'acces.fin.texte': 'Your jobs, applications and CV remain available. Nightly monitoring and new application packs will resume with a subscription.',
    'autreCompte.texte': 'This link comes from another account’s email.',
    'autreCompte.changer': 'Switch account',
    'autreCompte.rester': 'Stay here',
    'abonnement.titre': 'Subscription',
    'abonnement.sAbonner': 'Subscribe',
    'abonnement.redirection': 'Redirecting to payment…',
    'abonnement.parMois': 'per month',
    'abonnement.parAn': 'per year',
    'abonnement.ttc': 'VAT included',
    'abonnement.offert': 'Radar is offered to you.',
    'compteurs.titre': 'Your usage',
    'compteurs.cv': 'Tailored CVs this week',
    'compteurs.employeurs': 'Employers followed',
    'compteurs.recherches': 'Salary and interview prep this month',
    'compteurs.sur': 'of {max}',
    'compteurs.sansLimite': 'Unlimited',
    'compteurs.repart': 'Resets on {date}',
    'compteurs.atteint': 'Reached, resets on {date}',
    'compteurs.plein': 'Limit reached',
    'compteurs.avecComplet': 'With Complete',
    'abonnement.essaiJusqua': 'Free trial until {date}.',
    'abonnement.essaiFini': 'Your free trial ended on {date}.',
    'abonnement.duree': 'Billing period',
    'abonnement.auMois': 'Monthly',
    'abonnement.alAnnee': 'Yearly',
    'abonnement.radar': 'Radar',
    'abonnement.complet': 'Complete',
    'abonnement.radar.l1': '20 employers followed',
    'abonnement.radar.l2': '15 tailored CVs per week',
    'abonnement.complet.l1': 'All employers',
    'abonnement.complet.l2': 'Unlimited tailored CVs',
    'abonnement.complet.l3': 'Unlimited salary and interview prep',
    'abonnement.radar.l3': 'Salary and interview prep: 2 a month',
    'abonnement.radar.l4': '4 CV templates',
    'abonnement.complet.l4': 'My layout, taken from your CV',
    'abonnement.choisir': 'Choose {plan}',
    'abonnement.abonne': 'You are subscribed.',
    'abonnement.abonneJusqua': 'You are subscribed. Next renewal: {date}.',
    'abonnement.finLe': 'Your subscription ends on {date}.',
    'abonnement.gerer': 'Card, invoices, cancellation',
    'abonnement.reprendre': 'Resume subscription',
    'abonnement.enRetard': 'The last payment failed. Check your card.',
    'abonnement.mettreAJour': 'Update my card',
    'abonnement.confirmation': 'Payment received, confirming…',
    'abonnement.confirmationLongue': 'Confirmation is taking a while. Stripe will email it to you; reload this page in a few minutes.',
    'abonnement.annule': 'Payment cancelled. Nothing changed.',
    'abonnement.pasOuvert': 'Payment is not open yet.',
    'nav.config': 'Settings',
    'lang.toggle.label': 'Interface language',
    'nav.mainAria': 'Main navigation',
    'nav.logout': 'Log out',
    'nav.accountFallbackRadar': 'My account',
    'nav.accountFallbackConsole': 'Business Manager',
    'nav.consultantsCount': '{n} consultants',

    'login.radar.accroche': 'The jobs made for you, every morning.',
    'login.modes': 'Sign in or create an account',
    'login.ou': 'or',
    'login.mdp.voir': 'Show password',
    'login.mdp.masquer': 'Hide password',
    'inscription.titre': 'Create an account',
    'inscription.mdp': 'Choose a password',
    'inscription.mdpAide': 'At least {n} characters.',
    'inscription.envoyer': 'Create my account',
    'inscription.envoi': 'Sending…',
    'inscription.essai': 'One month free, no card.',
    'inscription.robot': 'Complete the anti-robot check.',
    'inscription.parti.texte': 'A link was just sent to {email}. Open it to activate your account.',
    'inscription.autreAdresse': 'Use another address',
    'inscription.activation': 'Activating your account…',
    'inscription.lienInvalide': 'This link is no longer valid. Sign up again.',
    'login.subtitle.console': 'Consultancy console',
    'login.subtitle.admin': 'Administration',
    'login.subtitle.radar': 'Radar',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.submitting': 'Signing in…',
    'login.submit': 'Sign in',
    'login.error.generic': 'Unable to sign in.',
    'login.oubli.lien': 'Forgot your password?',
    'login.google.bouton': 'Continue with Google',
    'login.google.sans_compte': 'No account for this Google address. Access is by invitation.',
    'login.google.expire': 'Signing in with Google timed out. Try again.',
    'login.google.annule': 'Signing in with Google was cancelled.',
    'login.google.erreur': 'Google could not confirm your identity. Try again.',
    'login.google.ferme': 'Sign-ups are closed for now.',
    'login.google.inscription': 'No account yet? Google creates it. One month free, no card.',
    'login.google.refuse': 'An account cannot be created with this Google address.',
    'login.oubli.titre': 'Your email address',
    'login.oubli.envoyer': 'Get a sign-in link',
    'login.oubli.parti': 'If an account exists for this address, a link is on its way. It is valid for 30 minutes.',
    'accueil.step': 'Step {n} of {total}',
    'accueil.next': 'Next',
    'accueil.back': 'Back',
    'accueil.finish': 'Finish',
    'accueil.done': 'Welcome. Everything is in Getting started if you want to read it again.',
    'accueil.radar.title': 'Radar searches while you do something else',
    'accueil.radar.lead': 'Sites re-read every night, on your behalf.',
    'accueil.radar.body': 'You pick job sites. Every night Radar re-reads them, keeps what is new, compares each posting with your CV and scores it. In the morning you are not searching: you are sorting what came up.',
    'accueil.radar.body2': 'An offer you like opens as a card: the detailed score, what matches, what is missing, and everything needed to generate a tailored CV and a cover letter.',
    'accueil.radar.shot1Alt': 'My opportunities screen: a list of offers with their score',
    'accueil.radar.shot2Alt': 'An offer card: detailed score, strengths and gaps',
    'accueil.extension.title': 'The Chrome extension works where you are',
    'accueil.extension.lead': "On LinkedIn, on Indeed, on an employer's own site: anywhere.",
    'accueil.extension.body': 'It is optional, but it is what goes with you onto postings: while you look at one, it shows your score on it, tailors your CV to that offer and drafts a letter. One click also sends it to Radar, to come back to later.',
    'accueil.extension.videoNote': 'Two minutes, and you will see exactly what it looks like.',
    'accueil.ensemble.title': 'Two independent tools, better together',
    'accueil.ensemble.lead': 'One that searches for you, one that follows you while you search.',
    'accueil.ensemble.passifTitle': 'Radar, the mode that runs by itself',
    'accueil.ensemble.passifBody': 'It re-reads your sites every night and prepares a pile for you. You do nothing for it to work.',
    'accueil.ensemble.actifTitle': 'The extension, the mode of the moment',
    'accueil.ensemble.actifBody': 'It acts on the posting in front of you, including on a site Radar does not watch.',
    'accueil.ensemble.body': 'Connected to each other, a button appears on every posting: one click and it lands in Radar with its score. The offer seen on LinkedIn at noon is in your history by the evening, and you decide whether to apply when you have time.',
    'accueil.ensemble.independants': 'You can use only one of the two. Neither depends on the other.',
    'accueil.cv.title': 'Your CV, once',
    'accueil.cv.lead': 'It is the reference everything is compared against.',
    'accueil.cv.body': 'Upload it as a PDF. It is read and structured once, then used to score every offer and to produce tailored CVs. Without it nothing can be compared and your screens will stay empty.',
    'accueil.cv.cta': 'Upload my CV',
    'accueil.cv.done': 'Your CV is in place.',
    'accueil.metiers.title': 'Your occupations',
    'accueil.metiers.lead': 'Here are the occupations we read in your CV.',
    'accueil.metiers.body': 'Radar only evaluates the offers of these occupations for you. Remove what does not fit, add what is missing: type an occupation and pick it from the list.',
    'accueil.metiers.enCours': 'Reading your CV, a few seconds…',
    'accueil.metiers.needCv': 'Upload your CV at the previous step first.',
    'accueil.metiers.later': 'You can change them at any time on your profile page.',
    'accueil.sites.title': 'Choose your sites',
    'accueil.sites.lead': 'Nothing is ticked: it is up to you to say what you want to follow.',
    'accueil.sites.body': 'These are the sites this Radar already knows how to read. Tick the ones that concern you: you will see only their offers, and changing your mind will not disturb anyone.',
    'accueil.sites.needCv': 'Upload your CV at the previous step to be able to choose: sites attach to your profile.',
    'accueil.sites.offers': '{n} offers',
    'accueil.sites.all': 'Tick all',
    'accueil.sites.none': 'Untick all',
    'accueil.sites.count': '{n} sites chosen',
    'accueil.sites.countMax': '{n} / {max} sites chosen',
    'accueil.sites.plafond': '{max} sites at most: untick one to pick another.',
    'accueil.sites.later': 'You can add, remove or request new ones at any time under Watched sites.',
    'accueil.revoir': 'Watch the introduction again',
    'aide.intro': 'What Radar is, how to connect the extension, and what will not work on the first try. This page stays here: come back whenever you need it.',
    'aide.what.title': 'What Radar does for you',
    'aide.what.body': 'Radar watches job listing pages every night, on your behalf. Every offer it finds is compared with your CV and gets a score. You stop searching: you look at what came up, and you decide.',
    'aide.what.extension': 'The Chrome extension is the other half: it works on any job site, shows your score right on the posting, tailors your CV, and sends the offer to Radar in one click. A posting seen on LinkedIn at noon is in Radar in the evening, with its score, without you writing anything down.',
    'aide.step.cv.title': '1. Your CV',
    'aide.step.cv.body': 'Everything starts here: with no CV, nothing can be compared. Upload it as a PDF, it is read and structured once. You can replace it at any time.',
    'aide.step.cv.cta': 'Upload or update my CV',
    'aide.step.install.title': '2. Install the extension',
    'aide.step.install.body': "Free, on the Chrome Web Store. One click, nothing to set up.",
    'aide.step.install.store': "Add to Chrome",
    'aide.step.install.storeS1': "On the listing, click Add to Chrome, then Add extension.",
    'aide.step.install.zipTitre': "Install without the Chrome Web Store",
    'aide.step.install.zipQuand': "Only if the store is blocked for you, on a work computer for example.",
    'aide.step.link.autrement': "No Connect with Radar button? Sign in with your Radar email and password, or with the code below.",
    'accueil2.extension.texte': "Your score on every job posting, LinkedIn included: the extension is on the Chrome Web Store.",
    'accueil2.extension.plusTard': "Later",
    'aide.step.install.download': 'Download the extension (version {version})',
    'aide.step.install.unavailable': 'The package for version {version} is not ready yet. Say so at the bottom of this page and you will get a message as soon as it is available.',
    'aide.step.install.s1': 'Unzip the downloaded file into a folder you will keep: Chrome reads the extension from there, and the bin would uninstall it.',
    'aide.step.install.s2': 'Open chrome://extensions in a new tab, then switch on Developer mode, top right.',
    'aide.step.install.s3': 'Click Load unpacked and pick the ai-job-fit-{version} folder INSIDE the unzipped folder, not the unzipped folder itself. If Chrome says the manifest is missing, that level is what is missing.',
    'aide.step.install.s4': 'Pin the icon to the toolbar: that is how you will switch the extension on for sites it does not know yet.',
    'aide.step.install.sites': 'It switches on by itself on LinkedIn, Indeed and the major recruiting tools. Elsewhere, Chrome requires your consent site by site: open the posting, click the extension icon, then Enable on this site. That is a Chrome rule, not a limit of the tool.',
    'aide.step.link.title': '3. Connect the extension to Radar',
    'aide.step.link.body': "Your resume then comes from Radar: nothing to import in the extension, and this is where you edit it.",
    'aide.step.link.lastUsed': 'Extension connected. Last delivery: {date}.',
    'aide.step.link.neverUsed': 'Extension connected, but no offer has arrived through it yet.',
    'aide.step.link.s1': "Open the extension: on first launch it opens by itself. Otherwise, right-click its icon, then Options.",
    'aide.step.link.s2': "Under the resume import, click Have a Radar account? Sign in, then Connect with Radar. Chrome asks for access to Radar: accept.",
    'aide.step.link.s3': 'Your resume shows up in the extension, and a Save to Radar button appears on every offer.',
    'aide.step.sites.title': '4. Add a site to watch',
    'aide.step.sites.body': 'Paste the address of a page that LISTS offers, not a single posting. Radar tests the extraction in front of you and shows the titles it found before saving anything.',
    'aide.step.sites.warning': 'Worth knowing upfront: it will not work on the first try for every site. Radar recognises the major platforms on its own; any other site needs one line of configuration written by hand. When that happens, a Request activation button appears: the request is recorded, you get a confirmation email, then a second one when the site is live, usually within a week. This is not a failure, it is how it works.',
    'aide.step.sites.cta': 'Go to my watched sites',
    'aide.shared.title': 'What is shared, and what is not',
    'aide.shared.body': 'Watched sites and collected offers are SHARED between the accounts of this Radar: you see the sites others added, and they see yours. Your CV, your scores, your applications and your documents are visible to you only.',
    'aide.feedback.title': 'A word to whoever builds Radar',
    'aide.feedback.body': 'Something is broken, a screen is confusing, a feature is missing, a site is not supported: write it here. The message goes out by email, with your address so the answer comes back to you.',
    'aide.feedback.typeLabel': 'What this is about',
    'aide.feedback.type.bug': 'Something is not working',
    'aide.feedback.type.idea': 'An idea, an improvement',
    'aide.feedback.type.site': 'A site to support',
    'aide.feedback.type.other': 'Something else',
    'aide.feedback.messageLabel': 'Your message',
    'aide.feedback.placeholder': 'What you were doing, what you expected, what happened instead.',
    'aide.feedback.send': 'Send',
    'aide.feedback.sending': 'Sending…',
    'aide.feedback.sent': 'Message sent. Thanks, you will get an answer at your address.',
    'aide.feedback.stored': 'Message recorded, but the email did not go out. It will be read all the same.',

    'password.title': 'Choose your password',
    'password.intro': 'This account was opened with a temporary password. It has to be replaced before anything else.',
    'password.current': 'Temporary password',
    'password.next': 'New password',
    'password.confirm': 'Confirmation',
    'password.rule': 'At least {n} characters. No other rule: a sentence you can remember beats a pile of symbols.',
    'password.submit': 'Save and continue',
    'password.submitting': 'Saving…',
    'password.tooShort': 'The new password must be at least {n} characters long.',
    'password.mismatch': 'The two entries do not match.',
    'password.error.generic': 'Unable to change the password.',
    'password.error.same': 'The new password must differ from the current one.',
    'password.logout': 'Sign out',

    'invite.title': 'Create your account',
    'invite.intro': 'This link opens a {produit} account for the address below. Choose your password to get started.',
    'invite.email': 'Account address',
    'invite.submit': 'Create my account',
    'invite.submitting': 'Creating…',
    'invite.loading': 'Checking the link…',
    'invite.gone.title': 'This link is no longer valid',
    'invite.gone.expiree': 'It has expired. Ask the person who sent it for a new one.',
    'invite.gone.utilisee': 'It has already been used to create an account. Sign in with the address and the password chosen then.',
    'invite.gone.revoquee': 'It has been replaced or cancelled. Ask the person who sent it for a new one.',
    'invite.gone.inconnue': 'This link is incomplete or wrong. Check that it was copied in full.',
    'invite.gone.compte_existant': 'An account already exists for this address. Sign in with its password.',
    'invite.otherProduct': 'This link opens an account on another site.',
    'invite.otherProductOpen': 'Open the right site',
    'invite.toLogin': 'Go to sign in',
    'invite.error.generic': 'Unable to create the account.',

    'candidature.stepToast': 'Stage: {label}',
    'candidature.propose': 'Put forward',
    'candidature.proposeName': 'Put {name} forward',
    'candidature.proposedToast': '{name} put forward, the application is in tracking',

    'common.loading': 'Loading…',
    'common.close': 'Close',
    'common.cancel': 'Cancel',
    'common.update': 'Update',
    'common.import': 'Import',

    'importCv.updateTitle': 'Update {name}',
    'importCv.importing': 'Importing…',
    'importCv.needFile': 'Choose a PDF file',
    'importCv.updatedToast': "{name}'s profile updated",
    'importCv.fileLabel': 'CV (PDF)',
    'importCv.depot': 'Drop the PDF here, or click to pick it',
    'importCv.fileHelp': 'Text extraction, profile structuring by the AI, then vectorisation for matching (~10 s). The job title is read from the CV.',
    'importCv.nameLabel': 'Name (optional)',
    'importCv.namePlaceholder': 'Otherwise: read from the CV',

    'gapQuestions.title': 'Before generating, {n} details',
    'gapQuestions.titleOne': 'Before generating, one detail',
    'gapQuestions.skip': 'Skip',
    'gapQuestions.generating': 'Generating…',
    'gapQuestions.introPlural': "The posting asks for things the CV doesn't say explicitly. Answering or not doesn't change whether the CV gets tailored, only what it can make the most of.",
    'gapQuestions.introSingular': "The posting asks for one thing the CV doesn't say explicitly. Answering or not doesn't change whether the CV gets tailored, only what it can make the most of.",
    'gapQuestions.placeholder': 'An experience worth mentioning, even a short one? Otherwise, leave it blank.',
    'gapQuestions.answeredNone': "Nothing filled in yet, tailoring will go ahead without enrichment.",
    'gapQuestions.answeredPlural': '{n} answers will be taken into account.',
    'gapQuestions.answeredSingular': '1 answer will be taken into account.',
    'gapQuestions.noneNeeded': 'No questions: the CV already covers this offer.',

    'vivier.loading': 'Loading the bench…',
    'vivier.wentOnMission': '{name} is now on a mission',
    'vivier.backOnBench': '{name} is back on the bench',
    'vivier.archivedToast': '{name} archived, history kept',
    'vivier.reactivatedToast': '{name} reactivated',
    'vivier.summary': '{available} available · {onMission} on a mission · {archived} archived',
    'vivier.showActive': 'View the active bench',
    'vivier.showArchived': 'Archived ({n})',
    'vivier.importCv': 'Import a CV',
    'vivier.avgScore': 'Avg. score',
    'vivier.evaluated': 'Evaluated',
    'vivier.positioned': 'Positioned',
    'vivier.updated': 'Updated',
    'vivier.reactivate': 'Reactivate',
    'vivier.onMission': 'On a mission',
    'vivier.onBench': 'On the bench',
    'vivier.archive': 'Archive',
    'vivier.dropzoneTitle': 'Import a PDF CV',
    'vivier.dropzoneHint': 'Extraction → structured profile → vectorisation. Evaluated against upcoming postings.',
    'vivier.noArchived': 'No archived consultant.',

    'dossier.revert': '↩ revert',
    'dossier.defautEnregistre': 'Settings saved for your next CVs.',
    'modeles.sobre': 'Plain',
    'modeles.dates': 'Dates on the left',
    'modeles.colonnes': 'Two columns',
    'modeles.colonnes.note': 'Two columns: some hiring software reads it poorly.',
    'modeles.compact': 'Compact',
    'modeles.perso': 'My layout',
    'modeles.perso.complet': 'With Complete',
    'modeles.perso.note': 'Taken from your CV. Word keeps the standard layout.',
    'modeles.perso.enCours': 'Being prepared',
    'modeles.perso.aPreparer': 'Prepare it in My CV',
    'modeles.parDefaut': 'This template will be used for your next CVs.',
    'config.modeleLabel': 'Layout',
    'config.modele.sobreEffet': 'One column, read by all hiring software.',
    'config.modele.datesEffet': 'Dates in a column, left of each job.',
    'config.modele.compactEffet': 'Tighter, to fit on fewer pages.',
    'config.modele.persoEffet': 'Taken from your CV, with Radar Complete.',
    'miseEnPage.titre': 'My layout',
    'miseEnPage.texte': 'Your tailored CVs can keep the layout of your own CV.',
    'miseEnPage.prete': 'Ready.',
    'miseEnPage.choix': 'The template of your next CVs, the same as in Settings.',
    'miseEnPage.enCours': 'Being prepared…',
    'miseEnPage.echec': 'It could not be taken from your CV.',
    'miseEnPage.preparer': 'Prepare my layout',
    'miseEnPage.refaire': 'Redo my layout',
    'miseEnPage.sansPdf': 'Upload your CV as a PDF to take its layout.',
    'miseEnPage.complet': 'With Radar Complete.',
    'miseEnPage.formules': 'See the plans',
    'miseEnPage.essai': 'Your trial includes one.',
    'dossier.revertedToast': 'Bullet reverted, audit recalculated',
    'dossier.loadingFile': 'Loading the document…',
    'dossier.notFound': 'Document not found',
    'dossier.regeneratingFile': 'Generating a new version…',
    'dossier.generatedOn': 'Generated {date}',
    'dossier.ouvrirCv': 'Open the CV',
    'dossier.savedToast': 'CV saved, audit recalculated',

    'categories.loadError': 'Unable to load',
    'categories.title': 'Categories',
    'categories.close': 'Close',
    'categories.edit': 'Edit',
    'categories.empty': "No category yet. Until there is one, this record isn't matched against any posting.",
    'categories.originHuman': 'corrected',
    'categories.originAi': 'suggested',
    'categories.neighbours': 'Neighbouring categories',
    'categories.neighboursHelp': "The sibling groups of the ones above. They don't trigger an automatic evaluation, but they say who's right next door.",
    'categories.selectedEmpty': 'No occupation yet. Type one below.',
    'categories.remove': 'Remove',
    'categories.searchLabel': 'Add an occupation',
    'categories.searchPlaceholder': 'Type an occupation: developer, nurse, accountant…',
    'categories.noResult': 'No occupation found. Try another word.',
    'categories.saving': 'Saving…',
    'categories.save': 'Save',
    'categories.savedToast': 'Categories saved',

    'suivi.loading': 'Loading tracking…',
    'suivi.activeCountPlural': '{n} applications in progress',
    'suivi.activeCountSingular': '{n} application in progress',
    'suivi.setAsideAt': 'put off {when}',
    'suivi.putBackTitle': 'Put back in « {name} »',
    'suivi.putBackToast': '« {title} » put back in « {name} »',
    'suivi.closedSummary': '{placed} {placedLabel}, {refused} refused',
    'suivi.offerGoneTitle': 'The posting has disappeared from the site',
    'suivi.offerExpired': 'expired posting',
    'suivi.noMovementTitle': 'No movement for {n} days',
    'suivi.daysShort': '{n}d',
    'suivi.openFileTitle': 'Open: {name}',
    'suivi.noFileTitle': 'No tailored CV has been generated for this application',
    'suivi.originalPosting': 'Original posting',
    'suivi.nextSend': 'Send the application',
    'suivi.preuveTitre': 'My sent applications',
    'suivi.preuveAide': 'Date, employer and role, for your job centre.',
    'suivi.preuveCopier': 'Copy the list',
    'suivi.preuveCopiee': 'List copied.',
    'suivi.preuveEchec': 'This browser won’t let Radar copy.',
    'suivi.nextFollowUp': 'Follow up on {date}',
    'suivi.nextFollowUpDue': 'Follow up now (due {date})',
    'suivi.nextInterview': 'Prepare the interview',
    'suivi.expiredTitle': 'Expired',
    'suivi.expiredHint': 'Postings taken down before anything was sent',
    'suivi.abandon': 'Drop',
    'suivi.abandonConfirm': 'Drop this application?',
    'suivi.abandonCancel': 'Cancel',
    'suivi.removeAriaTitle': 'Remove « {title} » from tracking',
    'suivi.removeAriaName': 'Remove {name} from tracking',
    'suivi.removedToastTitle': '« {title} » removed from tracking',
    'suivi.removedToastName': '{name} removed from tracking',

    'veille.periodAll': 'All dates',
    'veille.periodToday': 'Today',
    'veille.period7d': 'Last 7 days',
    'veille.period30d': 'Last 30 days',
    'veille.loading': 'Loading the watch…',
    'veille.subtitle': 'Everything the sources brought back · {n} postings tracked',
    'veille.subtitleSingular': 'Everything the sources brought back · {n} posting tracked',
    'veille.searchPlaceholder': 'Search…',
    'veille.searchAria': 'Search',
    'veille.clearSearch': 'Clear search',
    'veille.manualEntryButton': 'Manual entry',
    'veille.allCompanies': 'All companies',
    'veille.allLocations': 'All locations',
    'veille.allConsultants': 'All consultants',
    'veille.periodAria': 'Period',
    'veille.statusAria': 'Status',
    'veille.allStatuses': 'All statuses',
    'veille.shownPlural': '{n} postings shown',
    'veille.shownSingular': '{n} posting shown',
    'veille.reset': 'Reset ({n})',
    'veille.colTitle': 'Posting',
    'veille.colCompany': 'Company',
    'veille.colDetected': 'Detected',
    'veille.colStatus': 'Status',
    'veille.unread': 'Unread',
    'veille.missionBadge': 'Mission',
    'veille.viewOriginal': 'View the original posting',
    'veille.manualPlatform': 'manual entry',
    'veille.noMatch': 'No posting matches these filters.',
    'veille.resetFilters': 'Reset the filters',
    'veille.evaluating': 'Evaluating…',
    'veille.addedToast': 'Posting added, {n} profile(s) evaluated',
    'veille.addAndEvaluate': 'Add and evaluate',
    'veille.jobTitleLabel': 'Job title',
    'veille.jobTitlePlaceholder': 'E.g. Senior Backend Engineer, Java',
    'veille.companyLabel': 'Client / company',
    'veille.locationLabel': 'Location',
    'veille.optional': 'Optional',
    'veille.briefLabel': 'Brief',
    'veille.briefPlaceholder': 'Paste the full description here…',
    'veille.briefHelp': 'At least 100 characters. The posting is evaluated immediately.',

    'consultant.loading': 'Loading the record…',
    'consultant.backToBench': 'Back to the bench',
    'consultant.updatedOn': 'updated on {date}',
    'consultant.printPdf': 'Print / PDF',
    'consultant.noDocument': 'No document.',
    'consultant.originalPdf': 'Original PDF',
    'consultant.cvEdit': 'Edit',
    'consultant.cvSave': 'Save',
    'consultant.cvSaving': 'Saving…',
    'consultant.cvCancel': 'Cancel',
    'consultant.cvSaved': 'CV saved',
    'consultant.cvDiscard': 'Discard unsaved changes?',
    'consultant.keywordsTitle': 'What comes up in your postings',
    'consultant.keywordsIntroPlural': 'Across the {n} postings evaluated for you so far.',
    'consultant.keywordsIntroSingular': 'Across the {n} posting evaluated for you so far.',
    'consultant.keywordsCovered': 'What your CV already covers',
    'consultant.keywordsMissing': 'What was most often missing',
    'consultant.filesTotal': '{n} in total',
    'consultant.offerArchived': 'archived posting',
    'consultant.fidelityTitle': 'Fidelity to the master CV: {n}%',
    'consultant.noEvent': 'No event yet.',
    'consultant.eventFidelity': ', fidelity {n}%',
    'consultant.eventClient': ', {client}',

    'sources.addedToast': '{name} added, {n} postings inserted',
    'sources.alreadyKnownToast': 'Radar was already watching this site ({name}): you now follow it',
    'sources.noneFollowed': 'You do not follow any site yet. Paste the address of a careers page above, or browse the sites Radar already watches.',
    'sources.browseTitle': 'Browse the {n} sites Radar already watches',
    'sources.browseHelp': 'They are watched for other accounts. Follow the ones you care about: their postings will be compared with your resume.',
    'sources.browseSearch': 'Search a site or a company',
    'sources.browseNone': 'No site matches. Add it by its address, above.',
    'sources.loading': 'Loading sources…',
    'sources.title': 'Sources',
    'sources.activeSites': '{n} active sites',
    'sources.followedSites': '{n} sites followed',
    'sources.followedSitesMax': '{n} / {max} sites followed',
    'sources.errorCount': '{n} in error',
    'sources.watchedNightly': 'watched every night',
    'sources.addTitle': 'Add a site, paste the address of its careers page',
    'sources.urlPlaceholder': 'https://jobs.example.com',
    'sources.urlAria': 'Careers page URL',
    'sources.detect': 'Detect',
    'sources.detecting': 'Recognising the platform and testing extraction…',
    'sources.unreachablePrefix': 'Page',
    'sources.unreachable': 'unreachable',
    'sources.unreachableSuffix': ', check the URL.',
    'sources.unsupportedPrefix': 'Platform',
    'sources.unsupported': 'not supported yet',
    'sources.unsupportedSuffix': '. Covered today:',
    'sources.requestExplain': 'This is not the end of it: ask for activation below. An unknown site needs one line of configuration written by hand, which is the only part that cannot be guessed. You will get an email once it is live.',
    'sources.follow': 'Follow',
    'sources.logoLabel': 'Logo',
    'sources.logoUrlPlaceholder': 'Image address (https://…)',
    'sources.logoSet': 'Use',
    'sources.logoFile': 'Upload a file',
    'sources.logoAuto': "Use the site's favicon again",
    'sources.logoHelp': 'A logo set by hand is never replaced by the automatic search. PNG, SVG, ICO, JPEG or WebP, 512 KB at most.',
    'sources.logoUpdated': 'Logo updated.',
    'sources.unfollow': 'Stop following',
    'sources.followed': '{name}: you now follow this site. Radar is reading and scoring its offers, progress shows at the top of the screen.',
    'sources.followedStale': '{name}: you now follow this site. Nobody followed it, so its postings date from its last reading ({date}): Radar is reading it again now, then scoring its offers.',
    'sources.notReadSince': 'Read on {date}, re-read as soon as you follow it',
    'sources.neverRead': 'Read as soon as you follow it',
    'sources.staleState': 'Postings from {date}, new reading pending',
    'accueil.sites.staleHelp': 'Sites marked “not read” will be read again as soon as you finish, before their postings are scored.',
    'offre.staleSource': 'Read on {date}, not since: it may already be closed',
    'offre.staleShort': 'not read since {date}',
    'offre.republished': 'reposted (taken down on {date})',
    'offre.republishedMany': 'posted {n} times in 90 days',
    'offre.oldPosting': 'online for {n} days',
    'taches.cv': 'Your resume',
    'taches.phase.attente': 'waiting',
    'taches.phase.lecture': 'reading the offers',
    'taches.phase.classement': 'sorting by occupation',
    'taches.phase.evaluation': 'scoring the offers',
    'taches.phase.relecture': 're-reading the sites',
    'taches.unite.relecture': 'sites re-read: {fait} of {total}',
    'taches.unite.lecture': 'pages read: {fait} of {total}',
    'taches.unite.classement': 'offers sorted: {fait} of {total}',
    'taches.unite.evaluation': 'offers scored: {fait} of {total}',
    'taches.fini': 'done, {n} offer(s) scored',
    'taches.finiEnAttente': 'done, {n} offer(s) scored; {m} not readable yet, scored tonight',
    'taches.erreur': 'interrupted ({message}), tonight will pick it up',

    'sources.unfollowed': '{name}: you no longer follow this site. Its offers leave your screens and stay for the others.',
    'sources.followedLabel': 'Followed',
    'sources.notFollowedLabel': 'Not followed',
    'sources.request': 'Request activation',
    'sources.requesting': 'Sending…',
    'sources.requestNotePlaceholder': 'Optional: anything useful, for instance the page that lists the offers',
    'sources.requestNoteAria': 'Note for the request',
    'sources.requestSent': 'Request recorded. You get a confirmation email now, and a second one when the site is live, usually within {n} days.',
    'sources.requestSentNoMail': 'Request recorded, but the confirmation email did not go out. It is in the list below all the same.',
    'sources.requestFailed': 'Could not send the request.',
    'sources.pendingTitle': 'Sites awaiting activation',
    'sources.pendingHelp': 'Requested, not watched yet. You will get an email as soon as one goes live.',
    'sources.pendingNoMail': 'no email was sent for this request',
    'sources.recognizedPrefix': 'Platform recognised:',
    'sources.viaHtml': '(by reading the page)',
    'sources.offersFound': '{n} postings found',
    'sources.preview': 'preview',
    'sources.actuallyWatched': 'Portal actually watched:',
    'sources.adding': 'Adding…',
    'sources.addToWatch': 'Add to the watch',
    'sources.addAnyway': 'Add anyway',
    'sources.colCompany': 'Company',
    'sources.colPlatform': 'Platform',
    'sources.colHealth': 'Health',
    'sources.colLastCheck': 'Last check',
    'sources.colOffers': 'Postings',
    'sources.errorLabel': 'Error, {detail}',
    'sources.paused': 'Paused',
    'sources.active': 'Active',
    'sources.reactivatedToast': 'Source reactivated',
    'sources.pausedToast': 'Source paused',
    'sources.resume': 'Resume',
    'sources.pause': 'Pause',
    'sources.removeTitle': 'Remove from the watch',
    'sources.removedTitle': 'Removed from the watch',
    'sources.removedAt': 'removed {when}',
    'sources.wouldComeBack': '{n} postings would come back',
    'sources.restoredToast': '{name} put back in the watch, {n} posting(s) restored',
    'sources.restoreToWatch': 'Put back in the watch',
    'sources.removedToast': '{name} removed, {n} posting(s) archived',
    'sources.editTitle': 'Edit {name}',
    'sources.detecting2': 'Detecting…',
    'sources.saving': 'Saving…',
    'sources.updatedToast': 'Source updated',
    'sources.save': 'Save',
    'sources.companyLabel': 'Company',
    'sources.companyHelp': "The name shown everywhere else: on postings, in tracking, and in the name of exported CVs. Left blank, the source's own name is used.",
    'sources.sourceNameLabel': 'Source name',
    'sources.sourceNameHelp': 'To find your way in this list, when a company has several portals.',
    'sources.watchedUrlLabel': 'Watched URL',
    'sources.urlWillRedetect': "The platform will be re-detected at this address. If it isn't recognised, nothing will be saved.",
    'sources.currentPlatform': 'Current platform: {platform}.',
    'sources.removeConfirmTitle': 'Remove {name} from the watch?',
    'sources.removing': 'Removing…',
    'sources.removeFromWatch': 'Remove from the watch',
    'sources.removeConfirmP1': "It won't be queried at night anymore, and its {n} postings leave the watch.",
    'sources.removeConfirmP2': "Nothing is deleted: scores already computed, files generated and applications in progress stay intact, and the source can be put back in the watch in one click. That's what makes removing it safe.",

    'decisions.loading': 'Loading the pile…',
    'decisions.nothingPending': 'Nothing pending',
    'decisions.countPlural': '{n} postings',
    'decisions.countSingular': '{n} posting',
    'decisions.goodPlural': '{n} good matches',
    'decisions.goodSingular': '{n} good match',
    'decisions.lastRun': 'last run {when}',
    'decisions.searchAria': 'Search the pile',
    'decisions.onboardTitle': 'Radar has nothing to show you yet',
    'decisions.onboardIntro': "That's normal, your account was just created. Two things to do, once, and the watch runs every night without you having to think about it.",
    'decisions.onboardStep1Title': 'Upload your CV',
    'decisions.onboardStep1Help': "A PDF is enough. Radar extracts your experience and skills from it, in about twenty seconds. That's the CV every comparison is made against.",
    'decisions.onboardStep1Action': 'Upload a PDF',
    'decisions.onboardStep2Title': 'Choose employers to watch',
    'decisions.onboardStep2Help': "Paste the address of a careers page. Radar recognises the platform on its own and shows you what it can read before you confirm.",
    'decisions.onboardStep2Action': 'Add a site',
    'decisions.onboardNote': "The first night of watching starts as soon as the first site is added. You can close this, Radar keeps going.",
    'decisions.pileZeroText': 'Everything that came in has been decided on. Applications in progress are in',
    'decisions.pileZeroTracking': 'tracking',
    'decisions.pileZeroText2': 'and everything captured stays browsable in',
    'decisions.pileZeroWatch': 'the watch',
    'decisions.tierAria': 'Match tier',
    'decisions.allScores': 'All scores',
    'decisions.selectAll': 'Select all',
    'decisions.selectedPlural': '{n} selected',
    'decisions.selectedSingular': '{n} selected',
    'decisions.batchLater': 'set aside for later',
    'decisions.later': 'Later',
    'decisions.batchSkipped': 'skipped',
    'decisions.skip': 'Skip',
    'decisions.deselect': 'Deselect',
    'decisions.selectHint': "Check several postings to skip them at once. Opening a posting doesn't take it out of the pile.",
    'decisions.shownOfTotal': '{shown} of {total}',
    'decisions.noMatch': 'No posting in the pile matches these filters.',
    'decisions.resetShort': 'Reset',
    'decisions.selectOffer': 'Select {title}',
    'decisions.laterTitle': "I'll look at it again later",
    'decisions.reopenedToast': '« {title} » reopened, it goes back to be decided on',
    'decisions.skippedTitle': 'Skipped',
    'decisions.skippedHint': "Nothing is deleted, you can revisit the decision",
    'decisions.skippedAt': 'skipped {when}',
    'decisions.reopenTitle': 'Put back in the pile',
    'decisions.reopen': 'Reopen',
    'decisions.skippedToast': '« {title} » skipped',
    'decisions.laterToast': '« {title} » set aside for later',
    'decisions.appliedToast': '« {title} » added to your applications',
    'decisions.sortButton': 'Sort ({n})',
    'decisions.thresholdLabel': 'Show me from',
    'decisions.thresholdEffect': '{n} of {total} postings to sort',
    'decisions.thresholdSaved': 'Threshold saved: {n}',
    'tri.title': 'Sort my opportunities',
    'tri.progress': '{n} of {total}',
    'tri.close': 'Close',
    'tri.doneTitle': 'All sorted',
    'tri.doneText': '{n} postings sorted. The ones you follow are in your applications.',
    'tri.noProfile': 'No score on this posting: open it to evaluate it.',
    'decisions.batchToast': '{n} posting(s): {action}',
    'decisions.tierGood': 'Good match',
    'decisions.tierGoodRange': '70 and above',
    'decisions.tierMid': 'To check',
    'decisions.tierMidRange': '40 to 69',
    'decisions.tierLow': 'Weak match',
    'decisions.tierLowRange': 'below 40',

    'offre.loading': 'Loading the posting…',
    'offre.notFound': 'Posting not found',
    'offre.statusAriaLabel': 'Decision on this posting',
    'offre.kindMission': 'Mission',
    'offre.kindJob': 'Job',
    'offre.reperee': 'Found on {date}',
    'offre.avisEnFrancais': 'This assessment was written in French.',
    'offre.avisEnAnglais': 'This assessment was written in English.',
    'offre.avisEnNeerlandais': 'This assessment was written in Dutch.',
    'offre.avisEnItalien': 'This assessment was written in Italian.',
    'offre.avisEnEspagnol': 'This assessment was written in Spanish.',
    'offre.avisRetraduit': 'Radar will rewrite it in your language within a few nights.',
    'offre.avisFaux': 'Does this assessment look wrong?',
    'offre.avisFauxMerci': 'Thanks, noted.',
    'offre.avisPour': 'For you',
    'offre.avisAVerifier': 'To check',
    'offre.avisAdapte': 'With a tailored CV: {n}',
    'offre.exigenceCouverte': 'Covered',
    'offre.exigenceAConfirmer': 'To confirm',
    'offre.exigenceManquante': 'Missing',
    'offre.detailScore': 'Score details',
    'offre.annonceComplete': 'The full posting',
    'offre.evaluatedProfilesPlural': '{n} profiles evaluated',
    'offre.evaluatedProfilesSingular': '{n} profile evaluated',
    'offre.originalPosting': 'Original posting',
    'offre.deadlineValue': 'Deadline: {value}',
    'offre.startValue': 'Desired start: {value}',
    'offre.endValue': 'Expected end: {value}',
    'offre.notSetFem': 'not provided',
    'offre.notSetMasc': 'not provided',
    'offre.proposalsPlural': '{n} proposals submitted',
    'offre.proposalsSingular': '{n} proposal submitted',
    'offre.proposalsUnset': 'number of proposals not provided',
    'offre.frameworkUnset': 'framework agreement not provided',
    'offre.collapseDesc': 'Collapse the description',
    'offre.expandDesc': 'Read the full description',
    'offre.noDescriptionCaptured': 'No description captured.',
    'offre.applicationsHeader': 'Applications',
    'offre.proposedAgo': 'put forward {when}',
    'offre.viewFileLink': 'view the file',
    'offre.candidats': 'Candidates',
    'offre.aucunCandidat': 'No profile evaluated yet.',
    'offre.compareName': 'Compare {name}',
    'offre.scoreAriaLabel': '{name}: {score} out of 100',
    'offre.onDemandTitle': 'Evaluated on request, outside the pre-filter',
    'offre.gainAfterAdaptation': '↑ {gain} after tailoring',
    'offre.fileGeneratedTitle': 'File generated',
    'offre.compareHintBase': 'Check two or more profiles to compare them side by side,',
    'offre.compareOr': 'or',
    'offre.compareBest': 'compare the top {n}',
    'offre.compareFreeSuffix': " it's already free and instant, everything is already in the database.",
    'offre.compareMax': '{n} profiles at most in a comparison',
    'offre.journal': 'Posting journal',
    'offre.generatingFile': 'Generating the file for {name}…',
    'offre.generatingHelp': 'Faithful rewrite of the CV for the posting. Every change will be audited.',
    'offre.comparedCount': '{n} profiles compared on this posting',
    'offre.closeComparison': 'Close the comparison',
    'offre.generateTheirFile': 'Generate their file',
    'offre.applyTitle': 'Applying?',
    'offre.applyBody1': 'adds it to your applications, without computing anything.',
    'offre.adaptCvLabel': 'Tailor my CV',
    'quota.restantes': '{n} of {max} tailored CVs left this week',
    'quota.aZero': 'No tailored CV left this week. The counter resets on Monday.',
    'offre.applyBody2': "rewrites it for this posting: it's the only step that costs anything, and it only happens if you ask for it.",
    'offre.presentTitle': 'Ready to put {name} forward?',
    'offre.presentBody1': 'creates the application and tracks it, without calling anything.',
    'offre.theFileLabel': 'The file',
    'offre.presentBody2': "(CV rewritten for the posting + audit) is the only step that costs anything, and it's only generated if you ask for it.",
    'offre.alreadyInApplications': 'already in your applications',
    'offre.alreadyProposed': 'already put forward',
    'offre.about15s': '~15 s',
    'offre.generateAdaptedFile': 'Generate the tailored file',
    'offre.emptyAnalysis': 'No evaluation yet.',
    'offre.statusSkippedToast': 'Posting skipped',
    'offre.statusLaterToast': 'Set aside, it is in tracking',
    'offre.statusReopenedToast': 'Put back in decisions to make',
    'offre.neighboursTitle': 'Profiles in neighbouring categories',
    'offre.neighboursHelp': "They don't share this posting's category, but a sibling one. They aren't evaluated automatically.",
    'offre.scoredToast': '{name}: {score}/100',
    'offre.evaluate': 'Evaluate',
    'offre.notYetEvaluated': "This posting hasn't been evaluated for you yet",
    'offre.evaluatingLabel': 'Evaluating…',
    'offre.alreadyScoredToast': 'Already evaluated: {score}/100',
    'offre.evaluatedToast': 'Evaluated: {score}/100',
    'offre.evaluateMyProfile': 'Evaluate my profile',
    'offre.evaluateHelp': '~3 s, one AI call. The result is kept.',
    'offre.evaluateAnother': 'Evaluate another profile',
    'offre.employerUnknown': 'Employer not specified',
    'offre.employerViaSource': 'via {source}',
    'offre.radarSkipToast': 'Not for me: it won’t come back',
    'offre.radarLaterToast': 'Saved in My jobs. Radar will remind you on Monday {date} if it is still online.',
    'offre.radarReopenToast': 'Decision undone',
    'decisions.skippedToastRadar': '« {title} »: not for me',
    'decisions.reopenedToastRadar': '« {title} » is back in your opportunities',
    'sources.colState': 'Status',
    'sources.stateOk': 'OK',
    'sources.broken': 'Down',
    'sources.brokenSince': 'Down since {date}',
    'veille.notScored': 'Not scored',
    'offre.appliedToast': 'Added to your applications',
    'offre.descriptionTitle': 'The role',
    'offre.behindTheScenes': 'How Radar handled this posting',
    'offre.generatingMyCv': 'Tailoring your CV…',

    'config.loading': 'Loading the settings…',
    'config.saving': 'Saving…',
    'config.savedToast': 'Settings saved',
    'config.save': 'Save',
    'config.sliderAria': 'Minimum score to enter the pile',
    'config.scaleLow': '← see everything, including what doesn\'t fit',
    'config.scaleHigh': 'only see the best matches →',
    'config.computingEffect': 'Computing the effect…',
    'config.thresholdReference': 'For reference: the engine says "no" below 40, "maybe" up to 68, "yes" from 70.',
    'config.optimizationLabel': 'Tailoring level',
    'config.optimizationBalanced': 'Balanced',
    'config.optimizationTargeted': 'Targeted',
    'config.toneLabel': 'Tone',
    'config.toneSober': 'Sober',
    'config.toneDynamic': 'Dynamic',
    'config.toneExecutive': 'Executive',
    'config.lengthLabel': 'Length',
    'config.languageLabel': 'Language',
    'config.langue.auto': "The job's",
    'config.langue.autoEffet': "The job's language if your CV says you speak it, English otherwise.",
    'config.langue.fr': 'French',
    'config.langue.frEffet': 'Always in French.',
    'config.langue.en': 'English',
    'config.langue.enEffet': 'Always in English.',
    'config.length1Page': '1 page',
    'config.length2Pages': '2 pages',
    'config.lengthUnlimited': 'No limit',
    'config.questionsLabel': 'Questions before generating',
    'config.questionsOnce': 'Once per offer',
    'config.questionsAlways': 'Every time',
    'config.niveau.fidele': "Faithful",
    'config.niveau.audacieux': "Bold",
    'config.niveau.fideleEffet': "Keeps all your lines, rewrites and reorders them for the job.",
    'config.niveau.equilibreEffet': "Rewrites freely and trims a little of what serves the job less.",
    'config.niveau.cibleEffet': "Keeps what serves the job, trims the rest.",
    'config.niveau.audacieuxEffet': "May claim what the job asks for and your CV doesn’t say.",
    'config.ton.sobreEffet': "Plain sentences, no superlatives.",
    'config.ton.dynamiqueEffet': "Action verbs, results first.",
    'config.ton.executifEffet': "Leadership, vision, responsibilities.",
    'config.longueur.unePageEffet': "Only what matters for this job.",
    'config.longueur.deuxPagesEffet': "Concise, without cutting everything.",
    'config.longueur.sansLimiteEffet': "No length limit.",
    'config.questions.aide': "About what the job asks for and your CV doesn’t say, then about the letter’s angle.",
    'config.questions.uneFoisEffet': "Asked the first time for a job, then not again.",
    'config.questions.toujoursEffet': "Asked again before every CV and every letter.",
    'compte.motDePasse': "Password",
    'compte.changer': "Change",
    'compte.telecharger': "Download my data",
    'compte.supprimer': "Delete my account",
    'compte.supprimerAide': "Your CV, applications, tailored CVs and settings are erased. Invoices stay with Stripe. This cannot be undone.",
    'compte.supprimerConfirmer': "Delete for good",
    'compte.actuel': "Current password",
    'compte.motDePasseAide': "At least {n} characters. Your other devices will be signed out.",
    'compte.enregistrer': "Change password",
    'compte.actuelFaux': "The current password is wrong.",
    'config.signature': 'Signature',
    'config.authorLabel': 'Your name',
    // Lot 2 de la refonte de Radar (accueil, inscription, rythme des mails).
    'radar.moi': "Me",
    'radar.menuAria': "My account",
    'reglages.titre': "Settings",
    'mails.quand': "When",
    'marche.offres1': '1 job like yours this month',
    'marche.offres': '{n} jobs like yours this month',
    'marche.chez1': 'at 1 employer',
    'marche.chez': 'at {e} employers',
    'marche.salaire': 'Advertised pay: €{min} to €{max}.',
    'sugg.employeur': "{n} “Not this employer” for {nom}.",
    'sugg.employeurAction': "Mute {nom}",
    'sugg.regions': "{n} “Too far”.",
    'sugg.regionsAction': "Pick your regions",
    'sugg.salaire': "{n} turned down for salary.",
    'sugg.salaireAction': "Set a minimum",
    'sugg.metier': "{n} “Not my field”.",
    'sugg.metierAction': "Review your fields",
    'sugg.seuil': "Nothing has passed your threshold for a week. Your best jobs are at {n}.",
    'sugg.seuilAction': "Switch to {seuil}",
    'sugg.seuilFait': "Threshold set to {seuil}.",
    'cherche.placeholder': "Search all followed jobs",
    'cherche.rien': "No job.",
    'tri.ouvrir': "Quick sort",
    'tri.titre': "Quick sort · {n} / {total}",
    'tri.voir': "Open",
    'tri.plusTard': "Later",
    'tri.pasPourMoi': "Not for me",
    'tri.passer': "Next, decide later",
    'tri.fermer': "Close",
    'tri.fini': "All sorted.",
    'tri.plusTardOk': "Saved for later.",
    'tri.pasPourMoiOk': "Set aside.",
    'excl.cdd': "Fixed-term",
    'excl.interim': "Temp agency",
    'excl.freelance': "Freelance",
    'excl.stage': "Internship",
    'excl.junior': "Junior",
    'excl.senior': "Senior",
    'excl.langues': "Required languages I don’t speak",
    'excl.salaire': "Minimum gross yearly salary",
    'excl.contrats': "Contracts I accept",
    'excl.cdi': "Permanent",
    'excl.confirme': "Mid-level",
    'excl.niveaux': "Levels that suit me",
    'excl.teletravail': "Remote work",
    'excl.temps': "Working time",
    'excl.tempsPlein': "Full-time",
    'excl.tempsPartiel': "Part-time or 4/5",
    'excl.peuImporte': "Doesn’t matter",
    'excl.hybride': "At least hybrid",
    'excl.salaireAucun': "None",
    'excl.salaireAutre': "Other…",
    'excl.k': "€{n}k",
    'excl.parAn': "€ gross a year",
    'excl.jeParle': "I speak",
    'excl.parle.fr': "French",
    'excl.parle.nl': "Dutch",
    'excl.parle.en': "English",
    'excl.parle.de': "German",
    'reglages.cherche.titre': "What I’m looking for",
    'reglages.cherche.aide': "Applies to emails and the home page. A job that says nothing on a point always gets through.",
    'reglages.regions': "Where",
    'reglages.ecrit.titre': "When Radar writes to me",
    'reglages.avance': "Advanced",
    'reglages.compte.titre': "My account",
    'reglages.mails.coupesIci': "Emails are off. Turn them back on under “My account”.",
    'mails.matin': "The same morning",
    'mails.hebdo': "Mondays only",
    'mails.quels': "Which emails",
    'mails.mots': "Watched words",
    'mails.motsAide': "A new job containing one of them goes out by email, whatever its score. Ten at most, comma separated.",
    'mails.motsExemple': "Kubernetes, product owner",
    'mails.lundi': "Monday recap",
    'mails.relance': "Follow-up after 7 days",
    'mails.accueil': "Home threshold",
    'mails.commeMails': "Same as emails ({n})",
    'mails.saved': "Saved.",
    'mails.prioritaire': "Priority",
    'mails.muet': "Muted",
    'mails.prioritaireAide': "Every job in your fields goes out by email.",
    'mails.muetAide': "No more emails for this employer.",
    'reglages.mails.coupes': "You no longer get Radar emails. It keeps watching: your jobs stay here.",
    'reglages.mails.reactiver': "Get emails again",
    'reglages.mails.couper': "Stop all emails",
    'rythme.aria': "Email pace",
    'rythme.discret.nom': "Low-key",
    'rythme.discret.seuil': "80 and above",
    'rythme.discret.desc': "Only jobs very close to your CV.",
    'rythme.equilibre.nom': "Balanced",
    'rythme.equilibre.seuil': "70 and above",
    'rythme.equilibre.desc': "Jobs close to your CV.",
    'rythme.actif.nom': "I'm actively looking",
    'rythme.actif.seuil': "60 and above",
    'rythme.actif.desc': "Also jobs a little further away.",
    'rythme.estimationZero': "No job in the last 30 days",
    'rythme.estimationUn': "1 job in the last 30 days",
    'rythme.estimationPlus': "{n} jobs in the last 30 days",
    'rythme.note': "Counted on your employers over the last 30 days. One email a night at most.",
    'rythme.perso': "Your current threshold, {n}, was set by hand. Picking a pace replaces it.",
    'accueil2.sansCv': "Radar compares every job with your CV. Without it, there is nothing to show you.",
    'accueil2.sansCvAction': "Upload my CV",
    'accueil2.titreNouvelleUn': "One new job passes your threshold of {seuil}.",
    'accueil2.titreNouvellesPlus': "{n} new jobs pass your threshold of {seuil}.",
    'accueil2.titreAttenteUn': "One job above {seuil} is waiting for you.",
    'accueil2.titreAttentePlus': "{n} jobs above {seuil} are waiting for you.",
    'accueil2.titreCalme': "Nothing new above {seuil}.",
    'accueil2.aucunSuivi': "You are not following any employer yet.",
    'accueil2.aFaire': "To do",
    'accueil2.finFile': "That’s all for today.",
    'file.aria': "Jobs to sort",
    'file.position': "Job {rang} of {total}",
    'file.suivante': "Next",
    'file.terminer': "Done",
    'accueil2.etat': "Radar read your sites {quand}.",
    'accueil2.etatPremiere': "First read of your sites tonight.",
    'accueil2.lundi': "Recap on Monday, even with no news.",
    'accueil2.mailsCoupes': "You get no emails.",
    'accueil2.mailsReactiver': "Turn them back on",
    'accueil2.pause': "Emails paused until {date}",
    'accueil2.reprendre': "Resume now",
    'pause.titre': "Take a break",
    'pause.uneSemaine': "1 week",
    'pause.semaines': "{n} weeks",
    'pause.jusqua': "Paused until {date}",
    'accueil2.pourVous': "For you",
    'accueil2.pourVousPlus': "The best {n} out of {total}.",
    'accueil2.nouvelle': "New",
    'accueil2.via': "via {site}",
    'accueil2.atoutsLabel': "For you:",
    'accueil2.manquesLabel': "To check:",
    'accueil2.tranchee': "{etat}: {titre}",
    'accueil2.dessousTitre': "Just below",
    'accueil2.plusProches': "Closest matches",
    'accueil2.dessousUn': "1 job between {min} and {max} this week",
    'accueil2.dessousPlus': "{n} jobs between {min} and {max} this week",
    'accueil2.dessousNote': "They were not sent to you by email.",
    'accueil2.changerRythme': "Change my pace",
    'accueil2.choisirEmployeurs': "Choose my employers",
    'accueil2.empErreur': "Unreadable right now. The team knows, nothing to do on your side.",
    'accueil2.empAnnonces': "Job board: {n} jobs in your fields, the best at {m}",
    'accueil2.empAnnoncesRien': "Job board: nothing in your fields yet",
    'accueil2.empAnnoncesUn': "Job board: 1 job in your fields, at {m}",
    'accueil2.empRecrutePlus': "Hiring in your fields: {n} jobs, the best at {m}",
    'accueil2.empRecruteUn': "Hiring in your fields: 1 job, at {m}",
    'accueil2.empCalme': "Quiet in your fields.",
    'accueil2.enCoursUn': "1 application in progress",
    'accueil2.enCoursPlus': "{n} applications in progress",
    'bienvenue.etape': "Step {n} of 3",
    'bienvenue.cv.titre': "First, your CV",
    'bienvenue.cv.sous': "Radar reads it once, then uses it to compare every job. It stays private.",
    'bienvenue.essai': 'Radar is free for one month, no card needed.',
    'bienvenue.cv.extension': 'Your extension already has your CV.',
    'bienvenue.cv.reprendre': 'Use it',
    'bienvenue.cv.reprendreEchec': 'The extension’s CV could not be read.',
    'ext.installee': 'Your extension is installed.',
    'ext.connecter': 'Connect it',
    'ext.connectee': 'Extension connected.',
    'ext.echec': 'The connection failed. Try again.',
    'ext.echecSansCv': 'Upload your CV to Radar first.',
    'bienvenue.cv.depot': "Drop your CV as a PDF here",
    'bienvenue.cv.ou': "or tap to pick a file",
    'bienvenue.cv.lecture': "Radar is reading your CV, about twenty seconds…",
    'bienvenue.cv.lu': "Your CV has been read.",
    'bienvenue.cv.luNom': "Your CV has been read: {nom}.",
    'bienvenue.cv.metiers': "Radar will look for jobs as",
    'bienvenue.cv.metiersAide': "Remove what does not fit, add what is missing. Radar only scores jobs in these fields.",
    'bienvenue.cv.suivant': "That's me",
    'bienvenue.emp.titre': "Where would you say yes tomorrow?",
    'bienvenue.emp.sous': "Pick up to {max} sites. Radar reads their jobs every night. They are sorted by your fields.",
    'bienvenue.emp.sousSansMax': "Radar reads their jobs every night. They are sorted by your fields.",
    'bienvenue.emp.dansMetiers': "{n} in your fields",
    'bienvenue.emp.rien': "Nothing in your fields today",
    'bienvenue.emp.annoncesTitre': "Job boards",
    'bienvenue.emp.annoncesAide': "They post for hundreds of employers, and Radar only keeps your fields. Ticked by default: most good jobs come from them.",
    'bienvenue.emp.employeursTitre': "Employers",
    'bienvenue.emp.manque': "Your employer is not here? Add the address of its careers page from the Employers tab, once your radar is set up.",
    'bienvenue.continuer': "Continue",
    'bienvenue.retour': "Back",
    'bienvenue.rythme.titre': "When do you want to hear from us?",
    'bienvenue.rythme.pasEncore': "Radar is comparing your sites’ jobs with your CV right now. Your settings will show how much each pace sends, from tomorrow.",
    'bienvenue.fin': "Set up my radar",
    'offre.vpQuestion': "Applying?",
    'offre.vpOui': "Yes, prepare my CV",
    'offre.vpCvPret': "Your CV is ready.",
    'offre.vpSansAdapter': "Apply without tailoring my CV",
    'suivi.aDecider': "Your saved jobs",
    'suivi.aDeciderAide': "Saved from the extension or added by hand.",
    'suivi.gardeeLe': "Saved on {date}",
    'regions.question': "Where are you willing to work?",
    'regions.aide': "Nothing ticked: anywhere. A job elsewhere is never emailed, but stays visible at its employer. A job with no known location always goes through.",
    'regions.aria': "Regions",
    'regions.bruxelles': "Brussels",
    'regions.wallonie': "Wallonia",
    'regions.flandre': "Flanders",
    'regions.luxembourg': "Luxembourg",
    'regions.etranger': "Outside Belgium and Luxembourg",
    'filtre.titre': "What to follow at {nom}?",
    'filtre.aide': "{n} jobs online. Tick at least one area or language.",
    'filtre.aidePetit': "{n} jobs online. Nothing ticked: everything.",
    'filtre.aideTraduite': "{n} jobs, each published in several languages. Choose one.",
    'filtre.lecture': "Radar reads {n} jobs a night, newest first.",
    'emp.enLecture': "Reading in progress: newest first.",
    'filtre.zones': "Where",
    'filtre.langues': "Job ad language",
    'filtre.suivre': "Follow",
    'filtre.enregistrer': "Save",
    'filtre.enregistre': "Filter saved.",
    'filtre.changer': "Change the filter",
    'filtre.vide': "No readable area or language on this site.",
    'zones.brabant-wallon': "Walloon Brabant",
    'zones.hainaut': "Hainaut",
    'zones.liege': "Liège",
    'zones.luxembourg-be': "Luxembourg province",
    'zones.namur': "Namur",
    'zones.anvers': "Antwerp",
    'zones.brabant-flamand': "Flemish Brabant",
    'zones.flandre-occidentale': "West Flanders",
    'zones.flandre-orientale': "East Flanders",
    'zones.limbourg': "Limburg",
    'zones.france': "France",
    'zones.pays-bas': "Netherlands",
    'zones.allemagne': "Germany",
    'zones.suisse': "Switzerland",
    'zones.autre-pays': "Other countries",
    'langues.fr': "French",
    'langues.nl': "Dutch",
    'langues.en': "English",
    'langues.de': "German",
    'accueil2.horsRegionsUn': "1 more job above {seuil} is outside your regions.",
    'accueil2.horsRegionsPlus': "{n} more jobs above {seuil} are outside your regions.",
    'accueil2.exclueUn': "1 job left out by your criteria.",
    'accueil2.excluesPlus': "{n} jobs left out by your criteria.",
    'accueil2.changerCriteres': "Your criteria",
    'accueil2.changerRegions': "Change my regions",
    'emp.calmeDepuis': "Quiet in your fields. Last job {n} weeks ago.",
    'raison.question': "Why? (optional)",
    'aide.code.bouton': "Show a sign-in code",
    'aide.code.aide': "In the extension, Options, “Sign in with a Radar code”: type this code. No password to retype. Valid for 10 minutes, once.",
    'aide.code.aria': "Extension sign-in code",
    'aide.pont.ok': "Extension connected. You can close this tab.",
    'aide.pont.echec': "The extension could not connect. Try again from the extension.",
    "aideR.intro": "The extension, and what to do when something is stuck.",
    "aideR.ext.titre": "The extension",
    "aideR.ext.lead": "Your score on every job posting, LinkedIn included.",
    "aideR.ext.telephone": "It installs on a computer, in Chrome. Open Radar on your computer, then Help and extension.",
    "aideR.ext.dejaInstallee": "Already installed? Connect it",
    "aideR.ext.pasInstallee": "Not installed yet? Install it",
    "aideR.ext.pasDeBouton": "No Connect with Radar button? Use a code:",
    "aideR.ext.connectee": "Extension connected",
    "aideR.ext.nonConnectee": "The extension is not connected",
    "aideR.ext.rienEnvoye": "No job sent through it yet.",
    "aideR.ext.dernierEnvoi": "Last sent: {date}.",
    "aideR.ext.ailleurs": "On LinkedIn and Indeed it turns on by itself. Elsewhere, show it the site once:",
    "demo.pause": "Pause",
    "demo.lecture": "Play",
    "demo.reduit": "Animation off: reduce motion is on.",
    "demo.annuler": "Cancel",
    "demo.refuser": "Deny",
    "demo.autoriser": "Allow",
    "demo.install.titre": "Install and pin",
    "demo.install.aria": "Animation: on the Chrome Web Store, click Add to Chrome, confirm, then open the puzzle piece and pin the extension. Its icon shows up in the toolbar.",
    "demo.install.e1": "Add to Chrome",
    "demo.install.e2": "Add extension",
    "demo.install.e3": "The puzzle piece, then the pin",
    "demo.install.slogan": "Your score on every job",
    "demo.install.dlgTitre": "Add “AI Job Fit”?",
    "demo.install.dlgTexte": "It can read the job pages you open.",
    "demo.install.menu": "Extensions",
    "demo.install.ajouter": "Add extension",
    "demo.ac.bouton": "Enable on this site",
    "demo.ac.emp": "Example Hospital · Liège",
    "demo.co.titre": "Connect to Radar",
    "demo.co.aria": "Animation: in the extension options, click Have a Radar account?, then Connect with Radar, and allow. Radar confirms in a tab, and the CV arrives in the extension.",
    "demo.co.e1": "Have a Radar account?",
    "demo.co.e2": "Connect with Radar, then Allow",
    "demo.co.e3": "Radar confirms in a tab",
    "demo.co.e4": "Your CV is in the extension",
    "demo.co.import": "Import your resume to get started",
    "demo.co.depot": "Drop your CV",
    "demo.co.lien": "Have a Radar account? Sign in",
    "demo.co.compte": "I have a Radar account",
    "demo.co.bouton": "Connect with Radar",
    "demo.co.ouBien": "Or: password, or Radar code",
    "demo.co.perm": "Allow access to Radar?",
    "demo.co.fermer": "You can close this tab.",
    "demo.co.cv": "Your resume comes from Radar",
    "demo.co.nom": "Your name",
    "demo.co.metier": "Your job",
    "demo.co.bouton2": "Save to Radar shows up on every job.",
    "demo.ac.titre": "Enable on an unknown site",
    "demo.ac.aria": "Animation: on a job posting from an employer's site, click the extension icon, then Enable on this site, and allow. The score shows on the posting.",
    "demo.ac.e1": "The extension icon",
    "demo.ac.e2": "Enable on this site",
    "demo.ac.e3": "Allow, once per site",
    "demo.ac.e4": "The score shows on the posting",
    "demo.ac.poste": "Financial analyst",
    "demo.ac.pasAcces": "Not on for this site yet.",
    "demo.ac.perm": "Allow on this site?",
    "demo.ac.profil": "Good fit",
    "demo.ac.adapter": "Tailor my CV",
    "demo.ac.enregistrer": "Save to Radar",
    "demo.emp.titre": "An employer not followed yet",
    "demo.emp.aria": "Animation: you request activation, an email confirms right away, a second email says the employer is live, usually within a week.",
    "demo.emp.l1": "You ask",
    "demo.emp.l1s": "Request activation",
    "demo.emp.l2": "An email confirms",
    "demo.emp.l2s": "right away",
    "demo.emp.l3": "It is live",
    "demo.emp.l3s": "usually within a week",
    "aideR.faq.titre": "When something is stuck",
    "aideR.q.score": "No score on a job posting",
    "aideR.r.score": "On a site it does not know, the extension waits for your go-ahead. Once per site:",
    "aideR.r.scoreTel": "On a site it does not know, the extension waits for your go-ahead: click its icon, then Enable on this site. Once per site.",
    "aideR.q.reconnait": "The extension no longer knows me",
    "aideR.r.reconnait": "New computer, changed password: connect it again. In its options, Connect with Radar, or type this code:",
    "aideR.q.employeur": "An employer is not in the list",
    "aideR.r.employeur": "Add the address of its careers page in Employers. If Radar cannot read it yet, request activation. It is not a failure.",
    "aideR.cta.employeur": "Go to employers",
    "aideR.q.mails": "I do not get the emails",
    "aideR.r.mails": "Check your spam folder first. A work address sometimes filters Radar out: try a personal address.",
    "aideR.cta.adresse": "Change address",
    "aideR.q.rythme": "Too many emails, or too few",
    "aideR.r.rythme": "Change your pace. Each one says how many jobs it would have sent you.",
    "aideR.cta.rythme": "Change pace",
    "aideR.q.pause": "I am going on holiday",
    "aideR.r.pause": "Pause the emails for one, two or four weeks. They resume by themselves.",
    "aideR.cta.pause": "Pause emails",
    "aideR.q.supprimer": "Delete my account",
    "aideR.r.supprimer": "At the bottom of Settings. Your CV and scores are erased. You can download your data first.",
    "aideR.cta.compte": "Open my account",
    "aideR.prive.titre": "What stays private",
    "aideR.prive.texte": "Your CV, scores and applications are visible to you only. Followed employers and their jobs are shared by all accounts.",
    "aideR.ecrire.titre": "Write to us",
    "aideR.ecrire.texte": "A bug, an idea, a site to add: the message arrives by email, with your address for the answer.",
    'manques.titre': "What your jobs ask for that your CV doesn’t say",
    'manques.intro': "Across the {n} jobs in your fields over the last 30 days. If you have it, write it: your next scores will take it into account.",
    'manques.chez': "asked for at {liste}",
    'manques.sitesAnnonces': "job boards",
    'manques.jeLai': "I have it",
    'manques.ajoute': "“{mot}” added to your CV, under your skills.",
    'manques.annuler': "Undo",
    'manques.retire': "“{mot}” removed from your CV.",
    'manques.nAria': "Asked for by {n} jobs",
    'adresse.actuelle': "Radar writes to {email}.",
    'adresse.changer': "Use another address",
    'adresse.placeholder': "Your personal address",
    'adresse.envoyer': "Send the confirmation link",
    'adresse.aide': "A work address? Your job alerts would be read on your employer’s server. Another address is confirmed by a link.",
    'adresse.attente': "Waiting for confirmation: {attente}.",
    'adresse.statut.lienLe': "Link sent: {quand}.",
    'adresse.statut.envoye': "Brevo has it, no news yet from the receiving server.",
    'adresse.statut.livre': "Accepted by this address’s mail server.",
    'adresse.statut.differe': "Delayed by this address’s mail server, retrying.",
    'adresse.statut.refuse': "Rejected by this address’s mail server: {raison}",
    'adresse.statut.pasParti': "The link was not sent: {erreur}",
    'adresse.statut.pasRecu': "Not received? Check your junk folder or your mail quarantine.",
    'adresse.renvoyer': "Send the link again",
    'adresse.lienToast': "Link sent to {email}. Radar will write there once it is opened.",
    'adresse.compteToast': "Radar writes to your account address again.",
    'adresse.echecToast': "The link could not be sent. Try again later.",
    'adresse.confirmee': "Address confirmed: Radar will write there from now on.",
    'adresse.lienInvalide': "This confirmation link is no longer valid. Ask for a new one.",
    'parcours.aria': "Your application, step by step",
    'parcours.cv': "Your CV for {employeur}",
    'parcours.cvSans': "Your tailored CV",
    'parcours.cvPret': "Ready. View and edit it below.",
    'parcours.cvPretEstime': "Ready. Estimated score with this CV: {estime} instead of {score}.",
    'parcours.lettre': "Your cover letter (optional)",
    'parcours.lettreRediger': "Write the letter",
    'parcours.lettreNon': "I don’t need one",
    'parcours.postuler': "Apply at {employeur}",
    'parcours.postulerSans': "Apply on the posting",
    'parcours.ouvrirPage': "Open the application page",
    'parcours.dejaFait': "Done",
    'parcours.envoye': "Sent?",
    'parcours.envoyeOui': "Yes, it’s sent",
    'parcours.envoyeNon': "I won’t send it",
    'parcours.envoyeToast': "Noted: sent.",
    'parcours.envoyeNonToast': "Noted: it leaves your jobs.",
    'parcours.envoyeLe': "Sent on {date}. With no news by {relance}, Radar will suggest a follow-up.",
    'suivi.lienPlaceholder': "Paste a job posting link",
    'suivi.lienAria': "Job posting link",
    'suivi.lienAjouter': "Add",
    'suivi.lienLecture': "Reading the page…",
    'suivi.lienIllisible': "{raison} Paste its text below.",
    'offre.lienLater': "You chose “Later” in the email: confirm with one click.",
    'offre.lienSkip': "You chose “Not for me” in the email: confirm with one click.",
    'raison.loin': "Too far",
    'raison.metier': "Not my field",
    'raison.salaire': "Salary",
    'raison.employeur': "Not this employer",
    'raison.autre': "Other",
    'raison.merci': "Thanks, noted.",
    'suivi.rappelLe': "reminder on Monday {date}",
    'emp.pourVousUn': "1 job for you out of {n} in your fields, best {m}.",
    'emp.pourVousPlus': "{k} jobs for you out of {n} in your fields, best {m}.",
    'emp.uneSeule': "1 job in your fields, at {m}: it passes your threshold.",
    'emp.sousSeuil': "{n} jobs in your fields, none at {seuil} or more. Best: {m}.",
    'emp.sousSeuilUn': "1 job in your fields, at {m}: below your threshold of {seuil}.",
    'emp.prefixeAnnonces': "Job board · {phrase}",
    'emp.retour': "Employers",
    'emp.introuvable': "Radar no longer follows this employer.",
    'emp.suiviDepuis': "followed since {date}",
    'emp.dansMetiersTitre': "In your fields",
    'emp.etatCandidature': "In your applications",
    'emp.autresUn': "1 other job at {nom}",
    'emp.autresPlus': "{n} other jobs at {nom}",
    'emp.chercher': "Search these jobs",
    'emp.evaluer': "Score it for me",
    'emp.aucunResultat': "No job matches.",
    'emp.autresAide': "Outside your fields, Radar doesn’t score: it lists them so nothing slips by. “Score it for me” compares one job with your CV.",
    'emp.vide': "No job online at this employer right now.",
    'emp.illisibleNuits': "Unreadable for {n} nights. The team knows, nothing to do.",
    'emp.illisibleNuit': "Unreadable last night. The team knows, nothing to do.",
    'emp.apprentissage': "Learning: answer by {date}. Doesn’t count in your {max}.",
    'emp.apprentissageDepuis': "Learning since {date}. Doesn’t count in your {max}.",
    'emp.apprendLire': "Radar is learning to read {hote}. Answer by {date}.",
    'emp.apprendLireDepuis': "Radar has been learning to read {hote} since {date}.",
    'emp.refusee': "Radar can’t read {hote}.",
    'emp.rangeesIci': "Meanwhile, your {nom} jobs saved from the extension are filed here.",
    'emp.rangeesIciRefus': "Your {nom} jobs saved from the extension stay filed here.",
    'emp.suivisTitre': "Followed",
    'emp.enAttenteTitre': "Learning · don’t count in your {max}",
    'emp.enAttenteTitreSansMax': 'Learning',
    'emp.annoncesTitre': "Job boards",
    'emp.annoncesAide': "Hundreds of employers: Radar only keeps your fields and your regions.",
    'emp.suggeresTitre': "Suggested for you",
    'emp.suggereUn': "1 job in your fields",
    'emp.suggerePlus': "{n} jobs in your fields",
    'emp.luQuand': "Read {quand}",
    'emp.sousTitre': "Radar re-reads their pages every night · {n} of {max} sites followed",
    'emp.sousTitreSansMax': 'Radar re-reads their pages every night · {n} sites followed',
    'pont.texte': "You saved {n} jobs from {nom} with the extension. Put {nom} on your radar?",
    'pont.suivre': "Follow {nom}",
    'pont.ajouter': "Add their careers page",
    'pont.non': "No thanks",
    'pont.nonToast': "Radar won’t suggest it again.",
    'entretien.titre': "Prepare the interview",
    'entretien.aide': "The likely questions, and what your CV lets you answer.",
    'entretien.preparer': "Prepare",
    'entretien.redaction': "Preparing…",
    'entretien.refaire': "Redo",
    'entretien.aPoser': "To ask them",
    'entretien.aideSourcee': "Radar looks up the employer’s news and how it hires, then prepares the likely questions. Every source is cited. About a minute.",
    'entretien.enCours': "Radar is searching, about a minute. You can leave the page.",
    'entretien.erreur': "The search did not complete.",
    'entretien.actualite': "The employer right now",
    'entretien.deroule': "How it hires",
    'entretien.questions': "Likely questions",
    'entretien.tireeOffre': "From the job posting:",
    'entretien.rapportee': "Reported",
    'entretien.rienTrouve': "Nothing public and recent found about the employer.",
    'entretien.agence': "The job does not name the employer. If you know it, Radar will search for it.",
    'entretien.employeurNom': "Employer name",
    'entretien.avecEmployeur': "Search this employer",
    'entretien.parcours': "Application sent · Review the steps",
    'remu.titre': "Pay",
    'remu.encore': "{n} left this month.",
    'remu.plusDEssai': "None left this month.",
    'remu.aVolonte': "Unlimited with Complete.",
    'remu.avecComplet': "With Radar Complete.",
    'remu.comparer': "Compare",
    'remu.nonAnnonce': "Salary not stated",
    'remu.estimer': "Estimate",
    'remu.aide': "Radar looks up what the job states, the sector minimum and recent benchmarks, and cites every source. About a minute.",
    'remu.lancer': "Estimate the salary",
    'remu.enCours': "Radar is searching, about a minute. You can leave the page.",
    'remu.erreur': "The search did not complete.",
    'remu.reessayer': "Try again",
    'remu.offreDit': "What the job states",
    'remu.offreMuette': "The job states no amount.",
    'remu.minimum': "The sector minimum",
    'remu.auMoins': "{qui}: at least {montant}",
    'remu.sousEnPartie': "The bottom of the stated range is below this minimum.",
    'remu.sousEntierement': "The stated amount is below this minimum.",
    'remu.marche': "Market benchmarks",
    'remu.fourchette': "{fourchette}, from {n} sources that agree.",
    'remu.pasDeFourchette': "The benchmarks do not agree enough for a range.",
    'remu.aucunRepere': "No reliable benchmark found.",
    'remu.unRepere': "Only one reliable benchmark, no range.",
    'remu.quatreCinquiemes': "At 4/5: {fourchette}",
    'remu.avantages': "Benefits",
    'remu.aDemander': "To ask",
    'remu.agence': "The job does not name the employer: the benchmarks are for the role.",
    'remu.source': "Source: {qui}",
    'remu.sourceDatee': "Source: {qui}, {date}",
    'remu.sourceOffre': "the job posting",
    'remu.sources': "Sources: {retenues}, {ecartees}",
    'remu.retenue1': "1 kept",
    'remu.retenues': "{n} kept",
    'remu.ecartee1': "1 set aside",
    'remu.ecarteesN': "{n} set aside",
    'remu.ecartees': "Set aside",
    'remu.estimeLe': "Estimated on {date}.",
    'remu.refaire': "Redo",
    'remu.environ': "About {montant}",
    'remu.entre': "{a} to {b}",
    'remu.parHeure': "gross an hour",
    'remu.parMois': "gross a month",
    'remu.parAn': "gross a year",
    'remu.raison.page_fermee': "page closed or removed",
    'remu.raison.page_illisible': "unreadable page",
    'remu.raison.introuvable': "quote not found on the page",
    'remu.raison.trop_ancienne': "older than 18 months",
    'remu.raison.domaine_ecarte': "aggregator or copy",
    'remu.raison.sans_extrait': "no quote",
    'remu.raison.sans_source': "no address",
    'remu.raison.chiffre_absent': "figure missing from the quote",
    'remu.raison.autre_pays': "another country or currency",
    'remu.cadre': "Job country: {pays}. Gross yearly amounts.",
    'remu.fiabiliteTitre': "The employer",
    'remu.fiabilite': "{type}. Reliability of public figures: {fiab}.",
    'remu.soit': "(about {montant})",
    'remu.comp.base': "Base",
    'remu.comp.variable': "Variable",
    'remu.comp.avantage': "Benefit",
    'remu.fiab.haute': "high",
    'remu.fiab.moyenne': "medium",
    'remu.fiab.basse': "low",
    'remu.fiab.inconnue': "unknown",
    'remu.type.grand_groupe_tech': "Large tech company",
    'remu.type.startup_croissance': "Growth-stage start-up",
    'remu.type.startup_debut': "Early-stage start-up",
    'remu.type.entreprise_traditionnelle': "Established company",
    'remu.type.conseil_ou_agence': "Consulting firm or agency",
    'remu.type.pme_locale': "Local small business",
    'remu.type.commission': "Commission-based pay",
    'remu.type.recrutement_tiers': "Posted by a recruiter",
    'remu.type.public': "Public or non-profit sector",
    'remu.type.inconnu': "Unknown employer type",
    'relance.titre': "A week without news?",
    'relance.aide': "Sent on {date}. Radar only suggests one follow-up per application.",
    'relance.preparer': "Draft my follow-up",
    'relance.redaction': "Writing…",
    'relance.objet': "Subject: {sujet}",
    'relance.copier': "Copy",
    'relance.copie': "Follow-up copied.",
    'relance.envoyee': "I sent it",
    'relance.reecrire': "Rewrite",
    'relance.reponse': "I got an answer",
    'relance.reponseQuestion': "What answer?",
    'relance.entretien': "An interview",
    'relance.refus': "A rejection",
    'relance.abandon': "I’m dropping it",
    'relance.abandonQuestion': "Drop this application? It leaves My jobs.",
    'relance.abandonOui': "Yes, drop it",
    'relance.abandonToast': "Application removed from My jobs.",
  },
} as const

export type CleUi = keyof (typeof DICT)['fr']

/** Chaque langue de l'interface. Le français est la référence ; une clé
 *  absente d'une autre langue retombe sur lui. */
export const DICTS: Record<Lang, Partial<Record<CleUi, string>>> = { fr: DICT.fr, en: DICT.en, nl: DICT_NL, it: DICT_IT, es: DICT_ES }

/**
 * Les clés qui ne s'affichent QU'EN CONSOLE, et ont donc le droit de parler de
 * candidats, de vivier, de dossier ou de « proposer » (issue #77).
 *
 * POURQUOI UNE LISTE. Ce dictionnaire ne dépend pas de la formule : une clé y
 * sert les deux produits, sauf si l'écran ne l'appelle qu'en console. Rien ne
 * le disait, et « 1 profile evaluated », « Candidates » ou « put forward 3 d »
 * ont atteint des écrans Radar sans qu'aucun test ne bronche. Une clé déclarée
 * ici échappe au balayage de `vocabulaire.test.ts` ; toute autre y est tenue.
 * Ajouter une clé à cette liste, c'est affirmer que son écran est borné à la
 * console : `useT` le vérifie à l'exécution et le crie en Radar.
 */
export const CLES_CONSOLE: readonly CleUi[] = [
  'nav.accountFallbackConsole',
  'nav.consultantsCount',
  'candidature.propose',
  'candidature.proposedToast',
  'consultant.backToBench',
  'veille.allConsultants',
  'sources.removeConfirmP2',
  'offre.alreadyProposed',
  'offre.aucunCandidat',
  'offre.candidats',
  'offre.evaluatedProfilesPlural',
  'offre.evaluatedProfilesSingular',
  'offre.fileGeneratedTitle',
  'offre.generateAdaptedFile',
  'offre.generateTheirFile',
  'offre.generatingFile',
  'offre.proposedAgo',
  'offre.statusReopenedToast',
  'offre.theFileLabel',
  'offre.viewFileLink',
  'vivier.backOnBench',
  'vivier.loading',
  'vivier.noArchived',
  'vivier.onBench',
  'vivier.showActive',
]
const CONSOLE_SEULEMENT = new Set<string>(CLES_CONSOLE)
const dejaSignalees = new Set<string>()

/** `vars` : même mécanique que `useLabels()` (packages/ui) — substitution de
 *  `{cle}` dans la chaîne résolue. Nécessaire ici dès la première clé
 *  interpolée (`candidature.stepToast`), contrairement au dictionnaire
 *  générique posé en Phase 0 qui n'en avait pas encore besoin. */
export function useT(): (cle: CleUi, vars?: Record<string, string | number>) => string {
  const lang = useLang()
  const radar = useFormule() === 'radar'
  return (cle, vars) => {
    // Bruyant plutôt que silencieux : un libellé de consultance sur un écran
    // Radar ne casse rien, c'est précisément pourquoi il passait inaperçu.
    if (radar && CONSOLE_SEULEMENT.has(cle) && !dejaSignalees.has(cle)) {
      dejaSignalees.add(cle)
      console.error(`[vocabulaire] clé réservée à la console affichée en Radar : ${cle}`)
    }
    let out: string = DICTS[lang][cle] ?? DICT.fr[cle] ?? cle
    if (vars) for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, String(v))
    return out
  }
}
